/**
 * NotesTab - financial notes, stored per year.
 *
 * Deliberately not limited to RRSP and TFSA: this is a place for any thinking
 * about money that you want to find again later.
 */
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, ChevronDown, Plus, Trash2, Archive, Copy, MoreHorizontal, List, FileText, Link2, Check, Bell, Repeat } from "lucide-react";
import { Browser } from "@capacitor/browser";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { todayLocalKey, nowLocalIso } from "../../utils/plansUtils";
import { useSwipeBack } from "../../utils/swipeBack"; // plan_20
import { ensurePermission, syncReminderNotifications } from "../../utils/reminderNotifications";
import { requestAccess as requestAppleAccess, syncAll as syncApple, deleteCopy as deleteAppleCopy } from "../../services/appleRemindersService";
import {
  Sheet,
  DateField,
  SHEET_FIELD_LABEL,
  SHEET_INPUT_CLS,
  SHEET_INPUT_STYLE,
  SHEET_DATE_STYLE,
  PLAN_TILE_PALETTES,
  prettyDate,
  prettyShort,
  normalizePlans,
  ConfirmDialog,
} from "../plans/plansShared";
import BudgetSheets from "../plans/BudgetSheets";
import { EXAMPLE_ID } from "../plans/budgetSheetExample";
import { usePurple } from "../../utils/purple";
import { usePlan, PLAN_TILES, useMoneyPlanShown } from "../../utils/plan"; // plan_04, w2_01

const RICH_SLOTS = [0, 2, 3];

const RICH_ESCAPES = { '<': '&lt;', '>': '&gt;', '&': '&amp;' };

/**
 * Reduce arbitrary contentEditable output to exactly what we allow:
 * <b>, <br> and <span data-c="1|2|3">. Everything else becomes its text.
 * Block elements collapse to line breaks.
 */
function sanitizeRich(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString('<div>' + html + '</div>', 'text/html');
  const root = doc.body.firstChild;

  const walk = (node) => {
    let out = '';
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        out += (child.nodeValue || '').replace(/[<>&]/g, (m) => RICH_ESCAPES[m]);
        return;
      }
      if (child.nodeType !== 1) return;
      const tag = child.nodeName.toLowerCase();
      if (tag === 'br') { out += '<br>'; return; }
      const inner = walk(child);
      if (tag === 'b' || tag === 'strong') { out += inner ? '<b>' + inner + '</b>' : ''; return; }
      if (tag === 'span') {
        const slot = Number(child.getAttribute('data-c'));
        if (slot >= 1 && slot <= RICH_SLOTS.length && inner) {
          out += '<span data-c="' + slot + '">' + inner + '</span>';
        } else {
          out += inner;
        }
        return;
      }
      // Lists survive. An <ol> is folded into a <ul> rather than dropped, so
      // a browser that decides to make one never loses the content.
      if (tag === 'ul' || tag === 'ol') { out += inner ? '<ul>' + inner + '</ul>' : ''; return; }
      if (tag === 'li') { out += '<li>' + inner + '</li>'; return; }
      if (tag === 'div' || tag === 'p') {
        out += (out && !out.endsWith('<br>') ? '<br>' : '') + inner;
        return;
      }
      out += inner;
    });
    return out;
  };

  return walk(root);
}

/** Turn stored note markup into React nodes, coloured by the CURRENT theme. */
/**
 * flat = true renders list items as "- item" lines rather than real <ul>s.
 * Previews live inside a <span> in a <button> with a -webkit-box clamp, where
 * a block-level list is both invalid nesting and breaks the clamp.
 */
function richToNodes(html, palette, flat) {
  if (!html) return null;
  // Legacy plain-text notes, and anything without markup, render as-is.
  if (html.indexOf('<') === -1) return html;

  const doc = new DOMParser().parseFromString('<div>' + html + '</div>', 'text/html');
  const root = doc.body.firstChild;
  let key = 0;

  const walk = (node) => {
    if (node.nodeType === 3) return node.nodeValue;
    if (node.nodeType !== 1) return null;
    const tag = node.nodeName.toLowerCase();
    if (tag === 'br') return <br key={'r' + (key++)} />;
    const kids = Array.from(node.childNodes).map(walk);
    if (tag === 'b' || tag === 'strong') return <b key={'r' + (key++)}>{kids}</b>;
    if (tag === 'span') {
      const slot = Number(node.getAttribute('data-c'));
      if (slot >= 1 && slot <= RICH_SLOTS.length) {
        return (
          <span key={'r' + (key++)} style={{ color: palette[RICH_SLOTS[slot - 1]].value }}>
            {kids}
          </span>
        );
      }
      return <span key={'r' + (key++)}>{kids}</span>;
    }
    if (tag === 'ul' || tag === 'ol') {
      if (flat) return kids;
      return <ul key={'r' + (key++)}>{kids}</ul>;
    }
    if (tag === 'li') {
      if (flat) {
        // An explicit break, not a space. Under pre-wrap a space at a wrap
        // point survives into the next line and reads as an indent.
        return (
          <span key={'r' + (key++)}>
            {'\u2022 '}{kids}
            {node.nextElementSibling ? <br /> : null}
          </span>
        );
      }
      return <li key={'r' + (key++)}>{kids}</li>;
    }
    return kids;
  };

  return Array.from(root.childNodes).map(walk);
}

/**
 * The inverse of the sanitiser's colour handling.
 *
 * Stored markup carries only the token (data-c="2"), which contentEditable
 * cannot render. Before loading a note into the editor, paint the current
 * theme's colour back on as a real inline style so the user sees what they
 * wrote. sanitizeRich() strips it again on the way out, so the stored form
 * stays theme-independent.
 */
function hydrateRich(html, palette) {
  if (!html || html.indexOf('<') === -1) return html || '';
  const doc = new DOMParser().parseFromString('<div>' + html + '</div>', 'text/html');
  const root = doc.body.firstChild;
  root.querySelectorAll('span[data-c]').forEach((el) => {
    const slot = Number(el.getAttribute('data-c'));
    if (slot >= 1 && slot <= RICH_SLOTS.length) {
      el.style.color = palette[RICH_SLOTS[slot - 1]].value;
    }
  });
  return root.innerHTML;
}

const RichEditor = ({ initialHtml, onChange, palette, accent, track, minHeight, placeholder }) => {
  const ref = useRef(null);
  const [empty, setEmpty] = useState(!initialHtml);
  // What is switched on at the cursor right now. Without this a tap on B or a
  // swatch gives no feedback at all when the affected text is off screen.
  const [fmt, setFmt] = useState({ bold: false, list: false, slot: 0 });

  useEffect(() => {
    const read = () => {
      if (!ref.current) return;
      const sel = window.getSelection();
      if (!sel || !sel.anchorNode || !ref.current.contains(sel.anchorNode)) return;
      let bold = false;
      let list = false;
      try {
        bold = document.queryCommandState('bold');
        list = document.queryCommandState('insertUnorderedList');
      } catch (e) { /* older webviews */ }
      let node = sel.anchorNode;
      if (node.nodeType === 3) node = node.parentNode;
      const span = node && node.closest ? node.closest('span[data-c]') : null;
      const slot = span && ref.current.contains(span) ? Number(span.getAttribute('data-c')) : 0;
      setFmt({ bold, list, slot: slot >= 1 && slot <= RICH_SLOTS.length ? slot : 0 });
    };
    document.addEventListener('selectionchange', read);
    return () => document.removeEventListener('selectionchange', read);
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    // Uncontrolled on purpose. Writing innerHTML on every keystroke would
    // reset the caret to the start of the field.
    ref.current.innerHTML = hydrateRich(initialHtml, palette);
    try { document.execCommand('styleWithCSS', false, false); } catch (e) { /* older webviews */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sync = () => {
    if (!ref.current) return;
    setEmpty(!ref.current.textContent.trim());
    onChange(ref.current.innerHTML);
  };

  const applyBold = () => {
    if (ref.current) ref.current.focus();
    try { document.execCommand('bold'); } catch (e) { /* no-op */ }
    sync();
  };

  const applyBullets = () => {
    if (ref.current) ref.current.focus();
    try { document.execCommand('insertUnorderedList'); } catch (e) { /* no-op */ }
    sync();
  };

  const applyColor = (slot) => {
    if (ref.current) ref.current.focus();
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const hex = palette[RICH_SLOTS[slot - 1]].value;

    // If the selection covers an existing coloured span exactly, retag it in
    // place. Wrapping a second span inside the first is what made a colour
    // look like it could not be changed.
    let anc = range.commonAncestorContainer;
    if (anc && anc.nodeType === 3) anc = anc.parentNode;
    const existing = anc && anc.closest ? anc.closest('span[data-c]') : null;
    if (existing && ref.current && ref.current.contains(existing)
        && range.toString() === existing.textContent) {
      existing.setAttribute('data-c', String(slot));
      existing.style.color = hex;
      sel.removeAllRanges();
      sync();
      return;
    }

    // Otherwise wrap the selection, stripping any colour already inside it so
    // the result is one span rather than nested ones.
    const frag = range.extractContents();
    if (frag.querySelectorAll) {
      frag.querySelectorAll('span[data-c]').forEach((el) => {
        while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el);
        el.parentNode.removeChild(el);
      });
    }
    const span = document.createElement('span');
    span.setAttribute('data-c', String(slot));
    span.style.color = hex;
    span.appendChild(frag);
    range.insertNode(span);
    sel.removeAllRanges();
    sync();
  };

  return (
    <>
      <div className="flex items-center gap-2 mb-2">
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={applyBold}
          className="worthly-tool w-9 h-9 rounded-full flex items-center justify-center text-[15px] font-bold"
          style={{ background: fmt.bold ? accent : track, color: fmt.bold ? '#fff' : '#1F2937' }}
        >
          B
        </button>
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={applyBullets}
          className="worthly-tool w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: fmt.list ? accent : track, color: fmt.list ? '#fff' : '#1F2937' }}
        >
          <List className="w-4 h-4" />
        </button>
        <span style={{ width: '1px', height: '20px', background: '#EDE4E9' }} />
        {[1, 2, 3].map((slot) => (
          <button
            key={slot}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyColor(slot)}
            className="worthly-tool w-9 h-9 rounded-full flex items-center justify-center"
            style={{
              background: track,
              boxShadow: fmt.slot === slot
                ? '0 0 0 2px ' + palette[RICH_SLOTS[slot - 1]].value
                : 'none',
            }}
          >
            <span
              className="w-4 h-4 rounded-full block"
              style={{ background: palette[RICH_SLOTS[slot - 1]].value }}
            />
          </button>
        ))}
        <span className="text-[11.5px] text-gray-400 ml-1">Select text first</span>
      </div>

      <div style={{ position: 'relative' }}>
        {empty && placeholder && (
          <span
            className="text-[16px]"
            style={{ position: 'absolute', top: '13px', left: '16px', color: '#B9B0B6', pointerEvents: 'none' }}
          >
            {placeholder}
          </span>
        )}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={sync}
          onBlur={sync}
          className={SHEET_INPUT_CLS + ' worthly-rich'}
          style={{
            ...SHEET_INPUT_STYLE,
            minHeight,
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        />
      </div>
    </>
  );
};

// Sized to the two-across tile: four lines of 14px semibold at this width.
// Stored titles are never truncated to fit it - see NoteTile.
const NOTE_TITLE_MAX = 60;

// 'Reminder' retired once reminders became their own record. Its tone stays
// in NOTE_TAG_SLOTS below so notes already tagged that way still render.
const NOTE_TAGS = ['Decided', 'Considering', 'Guardrail'];

// Slots, not hexes. A tag resolves against whichever palette is live, so
// switching theme recolours existing notes instead of stranding them on the
// colours of the theme they were written under.
const NOTE_TAG_SLOTS = {
  Decided: 0,
  Reminder: 1,
  Guardrail: 2,
  Considering: 3,
};

const tagTone = (palette, tag) => {
  const slot = NOTE_TAG_SLOTS[tag];
  if (slot == null || !palette || !palette[slot]) return null;
  return { bg: palette[slot].bg, fg: palette[slot].value };
};

/** Pull the two colour stops out of a palette gradient string. */
const paletteStops = (bg) => String(bg).match(/#[0-9a-fA-F]{6}/g) || ['#EEE8EC', '#E2DAE0'];

/** Darken a hex by a factor - used for the folder tab behind the body. */
const shadeHex = (hex, factor) => {
  const n = parseInt(hex.slice(1), 16);
  const parts = [(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map((v) => Math.max(0, Math.min(255, Math.round(v * factor))).toString(16).padStart(2, '0'));
  return '#' + parts.join('');
};

// ---------------------------------------------------------------- plan_05
// Money plan: emoji, previews and section cards for the folder and note pages.
const PLAN_ROW_TONES = ['#DFE9F9', '#FAE4ED', '#DFF2E7', '#FCF3D2', '#ECE6FA'];
const PLAN_SECTION_INKS = ['#C04B7C', '#2F8756', '#7A5DD3', '#3C6DB5'];
const PLAN_EMOJI_EDGE = /^\s*(\p{Extended_Pictographic}(?:️|‍\p{Extended_Pictographic}|[\u{1F3FB}-\u{1F3FF}])*)\s*|\s*(\p{Extended_Pictographic}(?:️|‍\p{Extended_Pictographic}|[\u{1F3FB}-\u{1F3FF}])*)\s*$/u;
const PLAN_EMOJI_WORDS = [
  [/\b(credit|debit|cards?|visa|amex)\b/i, '💳'],
  [/subscri|netflix|spotify|\bstream/i, '🎬'],
  [/net ?worth|\bworth\b/i, '💰'],
  [/\bsav(e|es|ing|ings)\b|deposit|pay ?check|pay ?day|salary|\bincome/i, '💸'],
  [/rrsp|tfsa|fhsa|invest|\betfs?\b|stock|veqt|xeqt|vxc|portfolio|\bsplit/i, '📊'],
  [/\btax|\bcra\b|refund/i, '🧾'],
  [/\bdebts?\b|\bloans?\b|mortgage|\bbank/i, '🏦'],
  [/insurance/i, '🛡️'],
  [/\bcars?\b|\bgas\b/i, '🚗'],
  [/\brent\b|\bhouse|\bhome\b|\bmov(e|ing)\b/i, '🏠'],
  [/travel|\btrips?\b|flight|holiday|vacation|departure/i, '✈️'],
  [/budget/i, '📒'],
  [/\bgoals?\b|\bplans?\b|\bfuture\b/i, '🎯'],
  [/\bgifts?\b|birthday|christmas/i, '🎁'],
];

/** { emoji, text }: an emoji at either end of the name wins, else a guess. */
function planEmojiSplit(name, fallback, noGuess) {
  const s = String(name || '');
  let m = null;
  try { m = s.match(PLAN_EMOJI_EDGE); } catch (e) { m = null; }
  if (m && (m[1] || m[2])) {
    return { emoji: m[1] || m[2], text: s.replace(m[0], ' ').trim(), own: true };
  }
  const hit = noGuess ? null : PLAN_EMOJI_WORDS.find(([re]) => re.test(s));
  return { emoji: hit ? hit[1] : fallback, text: s, own: false };
}

/** One line of plain text from stored note markup, for row previews. */
function planPreview(body) {
  if (!body) return '';
  return String(body)
    .replace(/<br\s*\/?>|<\/?li>|<\/?ul>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Split a note into cards - plan_20.
 * Returns [{ title, slot, blocks: [{ type: 'text', html } | { type: 'sum', label, value }] }].
 * A line starts a new card when it's all bold or coloured (any length up to
 * 70 characters, even with an amount in it), a short line ending in ":" or a
 * short ALL CAPS line with no numbers. The card title keeps the colour you gave it (slot).
 * plan_21: lines with amounts stay as normal text, with no summary boxes.
 */
function planSections(body) {
  if (!body) return [];
  const raw = body.indexOf('<') === -1
    ? body.replace(/[<>&]/g, (m) => RICH_ESCAPES[m]).replace(/\n/g, '<br>')
    : body;
  const doc = new DOMParser().parseFromString('<div>' + raw + '</div>', 'text/html');
  const root = doc.body.firstChild;
  const lines = [];
  let cur = [];
  const flush = () => { lines.push(cur); cur = []; };
  Array.from(root.childNodes).forEach((n) => {
    const tag = n.nodeType === 1 ? n.nodeName.toLowerCase() : '';
    if (tag === 'br') { flush(); return; }
    if (tag === 'ul' || tag === 'ol') {
      if (cur.length) flush();
      lines.push([n]);
      return;
    }
    cur.push(n);
  });
  if (cur.length) flush();

  // Stray line breaks hidden in the saved text become spaces, so they never
  // show up as extra blank lines.
  const esc = (t) => String(t).replace(/\s*\n\s*/g, ' ').replace(/[<>&]/g, (m) => RICH_ESCAPES[m]);
  const toHtml = (nodes) => nodes.map((n) => (n.nodeType === 3 ? esc(n.nodeValue) : n.nodeType === 1 ? n.outerHTML : '')).join('');
  const textOf = (nodes) => nodes.map((n) => n.textContent || '').join('').replace(/\s+/g, ' ').trim();
  const isList = (nodes) => nodes.length === 1 && nodes[0].nodeType === 1 && /^(ul|ol)$/i.test(nodes[0].nodeName);
  // Every bit of text on the line is bold or coloured.
  const allStyled = (nodes) => {
    const solid = nodes.filter((n) => !(n.nodeType === 3 && !n.nodeValue.trim()));
    return solid.length > 0 && solid.every((n) => n.nodeType === 1 && /^(b|strong|span)$/i.test(n.nodeName));
  };
  // The colour you gave the line, if any (1-3, matching the editor's dots).
  const slotOf = (nodes) => {
    for (let i = 0; i < nodes.length; i += 1) {
      const n = nodes[i];
      if (n.nodeType !== 1) continue;
      const own = n.nodeName.toLowerCase() === 'span' ? Number(n.getAttribute('data-c')) : 0;
      if (own) return own;
      const inner = n.querySelector && n.querySelector('span[data-c]');
      if (inner) return Number(inner.getAttribute('data-c')) || 0;
    }
    return 0;
  };

  const headingOf = (nodes) => {
    if (isList(nodes)) return null;
    const t = textOf(nodes);
    if (!t || t.length > 70) return null;
    const colon = t.length <= 50 && /:$/.test(t);
    // ALL CAPS counts only for short lines with no numbers, so ticker lines like "VXC $9,609" stay as text.
    const caps = t.length <= 40 && /[A-Z]{2}/.test(t) && !/[a-z0-9]/.test(t);
    return allStyled(nodes) || colon || caps ? { title: t.replace(/:$/, '').trim(), slot: slotOf(nodes) } : null;
  };

  const sections = [];
  let sec = { title: '', slot: 0, items: [] };
  const push = () => {
    while (sec.items.length && sec.items[0].empty) sec.items.shift();
    while (sec.items.length && sec.items[sec.items.length - 1].empty) sec.items.pop();
    if (sec.title || sec.items.length) sections.push(sec);
  };
  lines.forEach((nodes) => {
    const head = headingOf(nodes);
    if (head) { push(); sec = { title: head.title, slot: head.slot, items: [] }; return; }
    sec.items.push({ html: toHtml(nodes), list: isList(nodes), empty: !isList(nodes) && !textOf(nodes) });
  });
  push();

  return sections.map((s) => {
    const blocks = [];
    let run = [];
    const endRun = () => {
      while (run.length && run[0].empty) run.shift();
      while (run.length && run[run.length - 1].empty) run.pop();
      if (!run.length) return;
      let html = '';
      run.forEach((part, i) => {
        const prev = run[i - 1];
        if (i > 0 && !part.list && !prev.list) html += '<br>';
        html += part.html;
      });
      blocks.push({ type: 'text', html });
      run = [];
    };
    s.items.forEach((it) => {
      if (it.sum) { endRun(); blocks.push({ type: 'sum', label: it.sum.label, value: it.sum.value }); return; }
      run.push(it);
    });
    endRun();
    return { title: s.title, slot: s.slot, blocks };
  });
}

const PlanNotePage = ({ note, palette, folderName, onEdit, onArchive, onClose }) => {
  const [menu, setMenu] = useState(false);
  // plan_20: swipe from the left edge to go back to your notes.
  const pageRef = useRef(null);
  useSwipeBack(() => pageRef.current, onClose);
  const { emoji, text } = planEmojiSplit(note.title, '📝');
  const sections = planSections(note.body);
  const meta = [folderName, note.createdAt ? prettyDate(note.createdAt) : null].filter(Boolean).join(' · ');
  const pill = { background: '#FFFFFF', borderRadius: '999px', height: '38px', padding: '0 16px', fontSize: '14px', fontWeight: 600, color: '#26252E', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
  return createPortal(
    <div
      className="worthly-plan-note"
      ref={pageRef}
      data-swipe-layer="1"
      style={{ position: 'fixed', inset: 0, zIndex: 70, background: '#F4F1FB', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
    >
      <div style={{ padding: 'calc(env(safe-area-inset-top, 0px) + 14px) 18px calc(env(safe-area-inset-bottom, 0px) + 40px)' }}>
        <div className="flex items-center justify-between" style={{ position: 'relative' }}>
          <button onClick={onClose} aria-label="Back" style={{ ...pill, width: '38px', padding: 0 }}>
            <ChevronLeft className="w-5 h-5" style={{ color: '#26252E' }} />
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => onEdit(note)} style={pill}>Edit</button>
            <button onClick={() => setMenu(!menu)} aria-label="More" style={{ ...pill, width: '46px', padding: 0 }}>
              <MoreHorizontal className="w-5 h-5" style={{ color: '#26252E' }} />
            </button>
          </div>
          {menu && (
            <div style={{ position: 'absolute', right: 0, top: '46px', background: '#FFFFFF', borderRadius: '16px', padding: '6px', boxShadow: '0 8px 30px rgba(40, 30, 80, 0.12)', zIndex: 2, minWidth: '190px' }}>
              <button
                onClick={() => { setMenu(false); onArchive(note.id, !note.archived); }}
                className="w-full flex items-center gap-2 text-left"
                style={{ padding: '11px 12px', fontSize: '14.5px', fontWeight: 600, color: '#26252E', borderRadius: '12px' }}
              >
                <Archive className="w-4 h-4" style={{ color: '#8B879A' }} />
                {note.archived ? 'Move back to active' : 'Archive'}
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3.5" style={{ marginTop: '22px' }}>
          <span style={{ width: '58px', height: '58px', borderRadius: '18px', background: '#FCF3D2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', flexShrink: 0 }}>
            {emoji}
          </span>
          <h1 className="m-0" style={{ fontSize: '25px', fontWeight: 700, letterSpacing: '-0.6px', lineHeight: 1.18, color: '#26252E', overflowWrap: 'anywhere' }}>
            {text || 'Note'}
          </h1>
        </div>
        {(meta || note.tag) && (
          <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: '12px' }}>
            {meta && <span style={{ fontSize: '13.5px', color: '#8B879A' }}>{meta}</span>}
            {note.tag && tagTone(palette, note.tag) && (
              <span className="text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: tagTone(palette, note.tag).bg, color: tagTone(palette, note.tag).fg }}>
                {note.tag}
              </span>
            )}
          </div>
        )}

        {sections.length === 0 && (
          <div style={{ background: '#FFFFFF', borderRadius: '24px', padding: '20px', marginTop: '20px', fontSize: '14.5px', color: '#8B879A' }}>
            Nothing written yet. Tap Edit to add to this note.
          </div>
        )}
        {sections.map((s, i) => (
          <div key={i} style={{ background: '#FFFFFF', borderRadius: '24px', padding: '20px', marginTop: i === 0 ? '20px' : '12px' }}>
            {/* plan_20: the title keeps the colour you gave it; semi-bold, normal case */}
            {s.title && (
              <p className="m-0" style={{ fontSize: '16px', fontWeight: 600, letterSpacing: '-0.2px', lineHeight: 1.35, color: s.slot && palette[RICH_SLOTS[s.slot - 1]] ? palette[RICH_SLOTS[s.slot - 1]].value : PLAN_SECTION_INKS[i % PLAN_SECTION_INKS.length], marginBottom: s.blocks.length ? '10px' : 0 }}>
                {s.title}
              </p>
            )}
            {s.blocks.map((b, j) => (b.type === 'sum' ? (
              <div key={j} style={{ background: '#ECE6FA', borderRadius: '18px', padding: '14px 16px', marginTop: j > 0 ? '12px' : 0 }}>
                <p className="m-0" style={{ fontSize: '13px', color: '#6C6879' }}>{b.label}</p>
                <p className="m-0" style={{ fontSize: '24px', fontWeight: 600, letterSpacing: '-0.5px', color: '#7A5DD3', marginTop: '2px' }}>{b.value}</p>
              </div>
            ) : (
              <div key={j} className="worthly-rich worthly-plan-rich" style={{ fontSize: '15px', lineHeight: 1.55, color: '#3A3944', whiteSpace: 'normal', overflowWrap: 'anywhere', marginTop: j > 0 ? '12px' : 0 }}>
                {richToNodes(b.html, palette)}
              </div>
            )))}
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
};

const NoteSheet = ({ note, kind, mode, accent, track, muted, palette, onSave, onArchive, onDelete, onEdit, onClose, folderName }) => {
  const actualKind = note ? note.kind : kind;
  const isPinned = actualKind === 'pinned';

  const [title, setTitle] = useState(note ? note.title || '' : '');
  const [body, setBody] = useState(note ? note.body || '' : '');
  const [tag, setTag] = useState(note ? note.tag || '' : '');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const valid = title.trim() || body.trim();
  const planPage = usePlan(); // plan_05

  // Reading a long note should not put a cursor in it. Edit is a deliberate
  // second step.
  if (mode === 'read' && note && planPage) {
    return (
      <PlanNotePage note={note} palette={palette} folderName={folderName}
        onEdit={onEdit} onArchive={onArchive} onClose={onClose} />
    );
  }
  if (mode === 'read' && note) {
    return (
      <Sheet
        title={note.title || 'Note'}
        subtitle={note.createdAt ? prettyDate(note.createdAt) : null}
        onClose={onClose}
        track={track}
        padBottom={96}
      >
        {note.tag && tagTone(palette, note.tag) && (
          <span
            className="inline-block text-[11px] font-bold px-2.5 py-1 rounded-full mb-3"
            style={{ background: tagTone(palette, note.tag).bg, color: tagTone(palette, note.tag).fg }}
          >
            {note.tag}
          </span>
        )}
        <div
          className="m-0 text-[14.5px] leading-relaxed worthly-rich"
          style={{ color: '#1F2937', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
        >
          {richToNodes(note.body, palette)}
        </div>
        <button
          onClick={() => onEdit(note)}
          className="w-full rounded-full py-4 mt-6 text-[15px] font-bold text-white"
          style={{ background: accent }}
        >
          Edit
        </button>
        <button
          onClick={() => onArchive(note.id, !note.archived)}
          className="w-full rounded-full py-3.5 mt-2.5 text-[14px] font-semibold border theme-border-100 flex items-center justify-center gap-1.5"
          style={{ color: '#6B7280' }}
        >
          <Archive className="w-4 h-4" />
          {note.archived ? 'Move back to active' : 'Archive'}
        </button>
      </Sheet>
    );
  }


  return (
    <Sheet
      title={note ? 'Edit note' : isPinned ? 'New pinned note' : 'New note'}
      subtitle={isPinned
        ? 'A short rule or decision.'
        : 'Room to write more details, press enter to start a new line.'}
      onClose={onClose}
      track={track}
    >
      <div className="mb-4">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>Title</label>
        <input
          className={SHEET_INPUT_CLS}
          style={{ ...SHEET_INPUT_STYLE, minHeight: '50px' }}
          maxLength={NOTE_TITLE_MAX}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        {title.length > NOTE_TITLE_MAX - 12 && (
          <p
            className="text-[12px] mt-1.5 text-right"
            style={{ color: title.length >= NOTE_TITLE_MAX ? '#B4485F' : '#a39fa0' }}
          >
            {title.length} / {NOTE_TITLE_MAX}
          </p>
        )}
      </div>

      <div className="mb-4">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>
          {isPinned ? 'Detail' : 'Note'}
        </label>
        <RichEditor
          initialHtml={body}
          onChange={setBody}
          palette={palette}
          accent={accent}
          track={track}
          minHeight={isPinned ? '90px' : '220px'}
        />
      </div>

      {isPinned && (
        <div className="mb-5">
          <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>
            Tag <span className="font-normal" style={{ color: muted }}>optional</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {NOTE_TAGS.map((t) => {
              const on = tag === t;
              const tone = tagTone(palette, t);
              return (
                <button
                  key={t}
                  onClick={() => setTag(on ? '' : t)}
                  className="text-[12px] font-bold px-3 py-2 rounded-full"
                  style={on
                    ? { background: tone.bg, color: tone.fg }
                    : { background: '#F7F1F5', color: '#6B7280' }}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <button
        disabled={!valid}
        onClick={() => onSave({
          ...(note || {}),
          kind: actualKind,
          title: title.trim(),
          body: sanitizeRich(body),
          tag: isPinned ? tag : '',
        })}
        className="w-full rounded-full py-4 text-[15px] font-bold text-white"
        style={{ background: valid ? accent : '#E0D6DC' }}
      >
        {note ? 'Save changes' : 'Save note'}
      </button>

      {note && (
        <>
          <button
            onClick={() => onArchive(note.id, !note.archived)}
            className="w-full rounded-full py-3.5 mt-2.5 text-[14px] font-semibold border theme-border-100 flex items-center justify-center gap-1.5"
            style={{ color: '#6B7280' }}
          >
            <Archive className="w-4 h-4" />
            {note.archived ? 'Move back to active' : 'Archive'}
          </button>

          <button
            onClick={() => setConfirmingDelete(true)}
            className="w-full rounded-full py-3.5 mt-2.5 text-[14px] font-semibold border flex items-center justify-center gap-1.5"
            style={{ color: '#E11D48', borderColor: '#F2DCE2' }}
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
          {confirmingDelete && (
            <ConfirmDialog
              title={'Delete \u201c' + (note.title || 'this note') + '\u201d?'}
              body="This note will be deleted for good. If you might want it later, archive it instead."
              confirmLabel="Delete for good"
              confirmColor="#E11D48"
              onCancel={() => setConfirmingDelete(false)}
              onConfirm={() => { setConfirmingDelete(false); onDelete(note.id); }}
            />
          )}
          <p className="text-[12px] text-gray-500 text-center mt-3 leading-snug">
            Archiving keeps the note. Deleting does not.
          </p>
        </>
      )}
    </Sheet>
  );
};

const BringForwardSheet = ({ notes, taxYear, accent, track, onCopy, onClose }) => {
  // Everything on by default, so taking the whole set stays a single tap.
  const [chosen, setChosen] = useState(() => notes.map((n) => n.id));
  const toggle = (id) => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  return (
    <Sheet
      title={'Bring forward to ' + taxYear}
      subtitle="Copies into this year. The originals stay where they are."
      onClose={onClose}
      track={track}
      padBottom={140}
    >
      {notes.map((n, i) => {
        const on = chosen.includes(n.id);
        return (
          <button
            key={n.id}
            onClick={() => toggle(n.id)}
            className={'w-full flex items-start gap-3 py-3 text-left' + (i === 0 ? '' : ' border-t theme-border-100')}
          >
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[11px] font-bold"
              style={{ background: on ? accent : '#E4DCE1', marginTop: '2px' }}
            >
              {on ? '\u2713' : ''}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[14px] font-semibold" style={{ color: '#1F2937' }}>
                {n.title || (n.kind === 'pinned' ? 'Pinned note' : 'Note')}
              </span>
              {n.body && (
                <span
                  className="block text-[12.5px] text-gray-500 mt-1 leading-snug"
                  style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                >
                  {String(n.body).replace(/<[^>]*>/g, ' ').trim()}
                </span>
              )}
            </span>
          </button>
        );
      })}

      <button
        disabled={chosen.length === 0}
        onClick={() => onCopy(chosen)}
        className="w-full rounded-full py-4 mt-5 text-[15px] font-bold text-white"
        style={{ background: chosen.length ? accent : '#E0D6DC' }}
      >
        {chosen.length === 0
          ? 'Nothing selected'
          : 'Copy ' + chosen.length + ' note' + (chosen.length === 1 ? '' : 's')}
      </button>
    </Sheet>
  );
};

const FolderSheet = ({ folder, palette, accent, track, muted, onSave, onDelete, onClose }) => {
  const [name, setName] = useState(folder ? folder.name : '');
  const [color, setColor] = useState(folder ? (folder.color || 0) : 0);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const valid = name.trim().length > 0;

  return (
    <Sheet
      title={folder ? 'Edit folder' : 'New folder'}
      subtitle="A folder to group your longer notes, folders carry across years."
      onClose={onClose}
      track={track}
    >
      <div className="mb-4">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>Name</label>
        <input
          className={SHEET_INPUT_CLS}
          style={{ ...SHEET_INPUT_STYLE, minHeight: '50px' }}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="mb-5">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>Colour</label>
        <div className="flex gap-2.5">
          {[0, 1, 2, 3].map((i) => (
            <button
              key={i}
              onClick={() => setColor(i)}
              className="flex-1 rounded-2xl flex items-center justify-center"
              style={{
                background: palette[i].bg,
                height: '52px',
                border: color === i ? '2px solid ' + palette[i].value : '2px solid transparent',
              }}
            >
              {color === i && (
                <span className="text-[15px] font-bold" style={{ color: palette[i].value }}>{'\u2713'}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <button
        disabled={!valid}
        onClick={() => onSave({ ...(folder || {}), name: name.trim(), color })}
        className="w-full rounded-full py-4 text-[15px] font-bold text-white"
        style={{ background: valid ? accent : '#E0D6DC' }}
      >
        {folder ? 'Save changes' : 'Create folder'}
      </button>

      {folder && (
          <>
            <button
              onClick={() => setConfirmingDelete(true)}
              className="w-full rounded-full py-3.5 mt-2.5 text-[14px] font-semibold border flex items-center justify-center gap-1.5"
              style={{ color: '#E11D48', borderColor: '#F2DCE2' }}
            >
              <Trash2 className="w-4 h-4" /> Delete folder
            </button>
            <p className="text-[12px] text-gray-500 text-center mt-3 leading-snug">
              Deleting a folder keeps its notes. They go back to Unfiled.
            </p>
            {confirmingDelete && (
              <ConfirmDialog
                title={'Delete the \u201c' + (folder.name || 'untitled') + '\u201d folder?'}
                body="The notes inside aren't deleted. They move back to Unfiled."
                confirmLabel="Delete folder"
                confirmColor="#E11D48"
                onCancel={() => setConfirmingDelete(false)}
                onConfirm={() => { setConfirmingDelete(false); onDelete(folder.id); }}
              />
            )}
          </>
      )}
    </Sheet>
  );
};

const MoveSheet = ({ note, folders, palette, accent, track, onMove, onClose }) => (
  <Sheet
    title="Move to folder"
    subtitle={note.title || 'Untitled note'}
    onClose={onClose}
    track={track}
    padBottom={140}
  >
    <button
      onClick={() => onMove(note.id, null)}
      className="w-full flex items-center gap-3 py-3.5 text-left"
    >
      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: '#D9D2D6' }} />
      <span className="flex-1 text-[14.5px]" style={{ color: '#1F2937' }}>No folder</span>
      {!note.folderId && (
        <span className="text-[14px] font-bold" style={{ color: accent }}>{'\u2713'}</span>
      )}
    </button>
    {folders.map((f) => (
      <button
        key={f.id}
        onClick={() => onMove(note.id, f.id)}
        className="w-full flex items-center gap-3 py-3.5 text-left border-t theme-border-100"
      >
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: palette[f.color || 0].value }} />
        <span className="flex-1 text-[14.5px] truncate" style={{ color: '#1F2937' }}>{f.name}</span>
        {note.folderId === f.id && (
          <span className="text-[14px] font-bold" style={{ color: accent }}>{'\u2713'}</span>
        )}
      </button>
    ))}
    {folders.length === 0 && (
      <p className="m-0 text-[13px] text-gray-500 leading-snug py-3">
        No folders yet. Create one from the Notes section.
      </p>
    )}
  </Sheet>
);

const REMINDER_NOTE_MAX = 300;

const REPEATS = [
  { key: 'never', label: 'Never' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'biweekly', label: '2 weeks' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'yearly', label: 'Yearly' },
];

export const isRepeating = (r) => !!r && !!r.repeat && r.repeat !== 'never';

export const repeatLabel = (k) => (
  k === 'weekly' ? 'Every week'
    : k === 'biweekly' ? 'Every 2 weeks'
      : k === 'monthly' ? 'Every month'
        : k === 'yearly' ? 'Every year'
          : ''
);

const lastDayOf = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/**
 * The next occurrence after `afterIso`.
 *
 * Steps rather than multiplies, so a monthly reminder anchored to the 31st
 * lands on the 30th in April and back on the 31st in May instead of drifting
 * earlier every month.
 *
 * Keeps stepping until it clears `afterIso`, so ticking off something three
 * months overdue moves it to the next date that is actually ahead - otherwise
 * it would reappear immediately, still overdue.
 */
export function advanceDue(dueAt, repeat, afterIso, repeatDay) {
  if (!dueAt || !repeat || repeat === 'never') return null;
  const time = dueAt.slice(11, 16) || '12:00';
  let y = Number(dueAt.slice(0, 4));
  let m = Number(dueAt.slice(5, 7));
  let d = Number(dueAt.slice(8, 10));
  // The anchor is stored, not read back off the current date. Re-deriving it
  // means a reminder on the 31st clamps to the 30th in September and is stuck
  // on the 30th forever after - the drift this stepping is meant to avoid.
  const anchorDay = Number(repeatDay) || d;
  const iso = () => y + '-' + String(m).padStart(2, '0') + '-' + String(d).padStart(2, '0') + 'T' + time;

  let guard = 0;
  do {
    if (repeat === 'weekly' || repeat === 'biweekly') {
      const t = new Date(Date.UTC(y, m - 1, d));
      t.setUTCDate(t.getUTCDate() + (repeat === 'biweekly' ? 14 : 7));
      y = t.getUTCFullYear(); m = t.getUTCMonth() + 1; d = t.getUTCDate();
    } else if (repeat === 'monthly') {
      m += 1;
      if (m > 12) { m = 1; y += 1; }
      d = Math.min(anchorDay, lastDayOf(y, m));
    } else {
      y += 1;
      d = Math.min(anchorDay, lastDayOf(y, m));
    }
    guard += 1;
  } while (iso() <= afterIso && guard < 600);

  return iso();
}

const ReminderSheet = ({ reminder, mode, accent, track, muted, onSave, onDelete, onEdit, onClose }) => {
  const [title, setTitle] = useState(reminder ? reminder.title || '' : '');
  const [date, setDate] = useState(reminder && reminder.dueAt ? reminder.dueAt.slice(0, 10) : '');
  const [time, setTime] = useState(reminder && reminder.dueAt ? reminder.dueAt.slice(11, 16) : '');
  const [note, setNote] = useState(reminder ? reminder.note || '' : '');
  const [link, setLink] = useState(reminder ? reminder.link || '' : '');
  const [repeat, setRepeat] = useState(reminder && reminder.repeat ? reminder.repeat : 'never');
  // plan_16: the reminder's own emoji ('' means pick one from the name).
  const plan = usePlan();
  const [emoji, setEmoji] = useState(reminder && reminder.emoji ? reminder.emoji : '');
  const [emojiTyped, setEmojiTyped] = useState(() => (reminder && reminder.emoji && !PLAN_REMINDER_EMOJIS.includes(reminder.emoji) ? reminder.emoji : ''));
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const valid = title.trim() && date;

  const openLink = async () => {
    const url = link.trim();
    if (!url) return;
    const full = /^https?:\/\//i.test(url) ? url : 'https://' + url;
    try {
      await Browser.open({ url: full });
    } catch (e) {
      window.open(full, '_blank');
    }
  };

  // Every hook above this line. An early return placed before them is exactly
  // what broke the build once already.
  if (mode === 'read' && reminder) {
    const due = reminder.dueAt || '';
    const at = due.slice(11, 16);
    return (
      <Sheet
        title={(plan && reminder.emoji ? reminder.emoji + ' ' : '') + (reminder.title || 'Reminder')}
        subtitle={due ? prettyDate(due.slice(0, 10)) + ' at ' + at : null}
        onClose={onClose}
        track={track}
        padBottom={96}
      >
        {reminder.note && (
          <p
            className="m-0 text-[14.5px] leading-relaxed"
            style={{ color: '#1F2937', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
          >
            {reminder.note}
          </p>
        )}
        {reminder.link && (
          <button
            onClick={openLink}
            className="w-full rounded-full py-3.5 mt-4 text-[14px] font-semibold border theme-border-100 flex items-center justify-center gap-1.5"
            style={{ color: accent }}
          >
            <Link2 className="w-4 h-4" /> Open link
          </button>
        )}
        <button
          onClick={() => onEdit(reminder)}
          className="w-full rounded-full py-4 mt-4 text-[15px] font-bold text-white"
          style={{ background: accent }}
        >
          Edit
        </button>
      </Sheet>
    );
  }

  return (
    <Sheet
      title={reminder ? 'Edit reminder' : 'New reminder'}
      subtitle="You'll get a notification at this time."
      onClose={onClose}
      track={track}
    >
      {/* plan_16: pick an emoji for this reminder (Money plan) */}
      {plan && (
        <div className="mb-4">
          <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>
            Emoji <span className="font-normal" style={{ color: muted }}>optional</span>
          </label>
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <button
              type="button"
              onClick={() => { setEmoji(''); setEmojiTyped(''); }}
              style={{ height: 42, padding: '0 12px', borderRadius: 14, border: 0, fontSize: 13.5, fontWeight: 600, color: '#6C6879', background: !emoji ? '#ECE6FA' : '#F7F5FB', boxShadow: !emoji ? 'inset 0 0 0 2px #B9A6F0' : 'none' }}
            >
              Pick for me {planRepeatEmoji(title)}{/* w2_11 */}
            </button>
            {PLAN_REMINDER_EMOJIS.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => { setEmoji(e); setEmojiTyped(''); }}
                style={{ width: 42, height: 42, borderRadius: 14, border: 0, fontSize: 19, background: emoji === e ? '#ECE6FA' : '#F7F5FB', boxShadow: emoji === e ? 'inset 0 0 0 2px #B9A6F0' : 'none' }}
              >
                {e}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={emojiTyped}
            onChange={(ev) => { const v = ev.target.value.slice(0, 8); setEmojiTyped(v); setEmoji(v.trim()); }}
            placeholder="Or type any emoji"
            className={SHEET_INPUT_CLS}
            style={{ ...SHEET_INPUT_STYLE, minHeight: '50px', marginTop: 10 }}
          />
          {!emoji && <p className="m-0 text-[12px] mt-1.5" style={{ color: muted }}>If you don't choose one, Worthly picks one that matches the reminder's name.</p>}{/* w2_12 */}
        </div>
      )}

      <div className="mb-4">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>Reminder</label>
        <input
          className={SHEET_INPUT_CLS}
          style={{ ...SHEET_INPUT_STYLE, minHeight: '50px' }}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="mb-4 flex gap-2">
        <div className="flex-1 min-w-0">
          <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>Date</label>
          <DateField
            className={SHEET_INPUT_CLS}
            style={{ ...SHEET_DATE_STYLE, textAlign: 'left' }}
            value={date}
            onChange={setDate}
          />
        </div>
        <div className="flex-1 min-w-0">
          <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>
            Time <span className="font-normal" style={{ color: muted }}>optional</span>
          </label>
          <DateField
            type="time"
            clearable
            className={SHEET_INPUT_CLS}
            style={{ ...SHEET_DATE_STYLE, textAlign: 'left' }}
            value={time}
            onChange={setTime}
          />
        </div>
      </div>

      <div className="mb-4">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>Repeat</label>
        <div className="flex gap-1 p-1 rounded-full" style={{ background: track }}>
          {REPEATS.map((o) => (
            <button
              key={o.key}
              onClick={() => setRepeat(o.key)}
              className="flex-1 py-2.5 rounded-full text-[12px] font-semibold whitespace-nowrap"
              style={
                repeat === o.key
                  ? { background: '#fff', color: accent, boxShadow: '0 1px 3px rgba(31,41,55,.08)' }
                  : { color: '#6B7280' }
              }
            >
              {o.label}
            </button>
          ))}
        </div>
        {repeat !== 'never' && (
          <p className="text-[12px] text-gray-500 mt-1.5 leading-snug">
            Ticking it off moves it to the next one rather than marking it done. It
            only sits under Overdue or Today when it is actually due; the rest of the
            time it waits under Repeating.
          </p>
        )}
      </div>

      <div className="mb-4">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>
          Note <span className="font-normal" style={{ color: muted }}>optional</span>
        </label>
        <textarea
          className={SHEET_INPUT_CLS}
          style={{ ...SHEET_INPUT_STYLE, minHeight: '86px', lineHeight: 1.5, resize: 'none' }}
          rows={3}
          maxLength={REMINDER_NOTE_MAX}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {note.length > REMINDER_NOTE_MAX - 40 && (
          <p className="text-[12px] mt-1.5 text-right" style={{ color: note.length >= REMINDER_NOTE_MAX ? '#B4485F' : '#a39fa0' }}>
            {note.length} / {REMINDER_NOTE_MAX}
          </p>
        )}
      </div>

      <div className="mb-5">
        <label className={SHEET_FIELD_LABEL} style={{ color: '#1F2937' }}>
          Link <span className="font-normal" style={{ color: muted }}>optional</span>
        </label>
        <input
          className={SHEET_INPUT_CLS}
          style={{ ...SHEET_INPUT_STYLE, minHeight: '50px', fontSize: '14px' }}
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          placeholder="canada.ca/..."
          value={link}
          onChange={(e) => setLink(e.target.value)}
        />
        {link.trim() && (
          <button
            onClick={openLink}
            className="w-full rounded-full py-3 mt-2 text-[14px] font-semibold border theme-border-100 flex items-center justify-center gap-1.5"
            style={{ color: accent }}
          >
            <Link2 className="w-4 h-4" /> Open link
          </button>
        )}
      </div>

      <button
        disabled={!valid}
        onClick={() => onSave({
          ...(reminder || {}),
          title: title.trim(),
          note: note.trim(),
          link: link.trim(),
          // Blank time is deliberate, not missing: midday is the least
          // intrusive default and never lands before someone is awake.
          dueAt: date + 'T' + (time || '12:00'),
          repeat,
          emoji: emoji || null, // plan_16
          // Anchored day of month, kept so a clamped month cannot move it
          // permanently earlier. Recomputed whenever the date is edited.
          repeatDay: repeat === 'never' ? null : Number(date.slice(8, 10)),
        })}
        className="w-full rounded-full py-4 text-[15px] font-bold text-white"
        style={{ background: valid ? accent : '#E0D6DC' }}
      >
        {reminder ? 'Save changes' : 'Add reminder'}
      </button>

      {reminder && (
        <>
          <button
            onClick={() => setConfirmingDelete(true)}
            className="w-full rounded-full py-3.5 mt-2.5 text-[14px] font-semibold border flex items-center justify-center gap-1.5"
            style={{ color: '#E11D48', borderColor: '#F2DCE2' }}
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
          {confirmingDelete && (
            <ConfirmDialog
              title={'Delete \u201c' + (reminder.title || 'this reminder') + '\u201d?'}
              body="This reminder and its notification will be deleted. This can't be undone."
              confirmColor="#E11D48"
              onCancel={() => setConfirmingDelete(false)}
              onConfirm={() => { setConfirmingDelete(false); onDelete(reminder.id); }}
            />
          )}
        </>
      )}
    </Sheet>
  );
};


// plan_15: Money plan Reminders (option A) - a week strip, date tiles and
// pastel tiles for repeats.
const PLAN_REM_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PLAN_REM_TONES = {
  overdue: { bg: '#FAE4ED', ink: '#C04B7C', ring: '#F2AFC8' },
  today: { bg: '#ECE6FA', ink: '#7A5DD3', ring: '#DCD3F4' },
  soon: { bg: '#DFE9F9', ink: '#3C6DB5', ring: '#DCD3F4' },
  later: { bg: '#FCF3D2', ink: '#7C7350', ring: '#DCD3F4' },
  done: { bg: '#F3F1F6', ink: '#A39FA0', ring: '#9ED9B6' },
};
// plan_16: emoji choices for a reminder.
const PLAN_REMINDER_EMOJIS = ['\u{1F514}', '\u{1F4B3}', '\u{1F3E0}', '\u{1F4F1}', '\u{1F6DF}', '\u{1F4C8}', '\u{1F9FE}', '\u{1F4A1}', '\u{1F697}', '\u2708\uFE0F', '\u{1F381}', '\u{1F4CA}'];
const PLAN_REPEAT_TONES = [['#DFF2E7', '#2F8756'], ['#DFE9F9', '#3C6DB5'], ['#ECE6FA', '#7A5DD3'], ['#FAE4ED', '#C04B7C']];
// A friendly emoji for a repeat, picked from its name.
const planRepeatEmoji = (title) => {
  const t = String(title || '').toLowerCase();
  const pairs = [
    [/rent|mortgage|condo/, '\u{1F3E0}'], [/phone|mobile|cell/, '\u{1F4F1}'], [/internet|wifi|wi-fi/, '\u{1F4F6}'],
    [/car|insurance/, '\u{1F697}'], [/hydro|electric|utilit|water|gas bill/, '\u{1F4A1}'], [/gym|fitness|class/, '\u{1F3CB}\u{1F3FE}‍♀️'],
    [/save|saving|eq\b|hisa|fund/, '\u{1F6DF}'], [/invest|tfsa|rrsp|fhsa|ws\b/, '\u{1F4C8}'], [/credit|card|visa|amex|mastercard/, '\u{1F4B3}'],
    [/subscri|netflix|spotify|crave|disney|apple/, '\u{1F4FA}'], [/pay|bill/, '\u{1F9FE}'], [/budget|net worth|review/, '\u{1F4CA}'],
  ];
  const hit = pairs.find(([re]) => re.test(t));
  return hit ? hit[1] : '\u{1F501}';
};
const planDayDiff = (fromKey, toKey) => {
  const a = new Date(fromKey + 'T12:00:00');
  const b = new Date(toKey + 'T12:00:00');
  return Math.round((b - a) / 86400000);
};
const planTimeParts = (iso) => {
  const t = String(iso || '').slice(11, 16);
  if (!t) return null;
  const [h, mm] = t.split(':').map(Number);
  if (!Number.isFinite(h)) return null;
  return { big: ((h % 12) || 12) + (mm ? ':' + String(mm).padStart(2, '0') : ''), small: h < 12 ? 'am' : 'pm' };
};

const NotesTab = ({ plans, moneyTimeline, timelineFunds = [] /* plan_13 */, saveUserData, theme = 'pink', remindersEnabled = true, onShowHelp, onBack, section, moneyPlan = null, onOpenCheckup = null /* plan_27 */ }) => {
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  // Purple (admin-only): lavender instead of hot pink and multicolour tiles.
  const purple = usePurple();
  // plan_04: Money plan - calm headings, soft fills, pastel tiles.
  const plan = usePlan();
  const eyebrow = plan ? '#26252E' : purple ? '#7B5AA6' : isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
  const accent = plan ? '#7A5DD3' : purple ? '#8B5CE0' : isPink ? '#e91e8c' : isWarm ? '#9B6B6B' : '#4361EE';
  const track = plan ? '#F5F2FA' : purple ? '#F3ECFB' : isPink ? '#F3ECF7' : isWarm ? '#F0E6D8' : '#EEF1FE';
  const muted = '#a39fa0';
  const tilePalette = plan ? PLAN_TILES : PLAN_TILE_PALETTES[purple ? 'purple' : isPink ? 'pink' : isWarm ? 'warm' : 'blue'];
  const cardCls = "bg-white rounded-2xl border theme-border-100 shadow-sm";
  const serif = { fontFamily: "'DM Serif Display', serif", fontWeight: 400 };

  const [local, setLocal] = useState(() => normalizePlans(plans));
  useEffect(() => { setLocal(normalizePlans(plans)); }, [plans]);

  const persist = (next) => {
    setLocal(next);
    saveUserData({ plans: next });
  };

  const todayKey = todayLocalKey();
  const [taxYear, setTaxYear] = useState(() => new Date().getFullYear());
  const [noteSheet, setNoteSheet] = useState(null);
  // plan_12: a timeline step on Home can open its note here.
  useEffect(() => {
    try {
      const id = localStorage.getItem('worthly_open_note');
      if (!id) return;
      localStorage.removeItem('worthly_open_note');
      const n = (local.notes || []).find((x) => x.id === id);
      if (n) setNoteSheet({ note: n, kind: n.kind, mode: 'read' });
    } catch (e) { /* private mode */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [tab, setTab] = useState(() => {
    // Each of Notes / Reminders / Budget Sheets is its own page now; the
    // route decides which one this is.
    if (section) return section;
    // Set by the overdue bar on Home. Read once and cleared, so it only
    // affects the navigation that requested it.
    try {
      if (localStorage.getItem('worthly_notes_open_reminders') === '1') {
        localStorage.removeItem('worthly_notes_open_reminders');
        return 'reminders';
      }
    } catch (e) { /* private mode */ }
    return 'notes';
  });
  const [showArchived, setShowArchived] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [showLater, setShowLater] = useState(true);
  const [checkupSheet, setCheckupSheet] = useState(false); // plan_27
  const moneyPlanShown = useMoneyPlanShown(); // w2_01
  const [showRepeating, setShowRepeating] = useState(true);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [appleSheet, setAppleSheet] = useState(false);
  const [appleBusy, setAppleBusy] = useState(false);
  // busy: stops overlapping passes. ids: what this session has already
  // exported, for the window after a pass finishes but before its persist has
  // come back round through local state.
  const appleRunRef = useRef({ busy: false, ids: {} });
  const [reminderSheet, setReminderSheet] = useState(null);   // { reminder|null }
  const [showInfo, setShowInfo] = useState(false);
  const [copySheet, setCopySheet] = useState(false);
  const [folderSheet, setFolderSheet] = useState(null);   // { folder|null }
  const [moveSheet, setMoveSheet] = useState(null);       // { note }
  const [openFolder, setOpenFolder] = useState(null);     // folder id
  const [showAllPinned, setShowAllPinned] = useState(false);
  // An open budget sheet takes over the page like an open folder does. Only
  // counts while that sheet still exists, so deleting it can never leave the
  // page with no header and no tab bar.
  const [openBudget, setOpenBudget] = useState(null);
  const budgetOpen = tab === 'budgets'
    && (openBudget === EXAMPLE_ID || (local.budgetSheets || []).some((s) => s.id === openBudget));
  // The Budgets tab has its own explainer; the header i opens it on that tab.
  const [budgetInfo, setBudgetInfo] = useState(false);

  // Opening a folder from halfway down the page used to land you halfway down
  // the folder. The app scrolls an inner container, not the window, so find
  // whichever ancestor actually scrolls and reset that.
  const rootRef = useRef(null);
  // plan_20: swipe back - a folder goes back to your notes, and Notes,
  // Reminders or Budgets go back to Home (Home stays underneath).
  const folderLayerRef = useRef(null);
  useSwipeBack(() => folderLayerRef.current, () => setOpenFolder(null), plan);
  useSwipeBack(() => (rootRef.current ? rootRef.current.closest('.wy-notes-layer') : null), () => { if (onBack) onBack(); }, plan && !!onBack && !openBudget && !openFolder);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    let node = el.parentElement;
    while (node) {
      const overflow = window.getComputedStyle(node).overflowY;
      if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight) {
        node.scrollTop = 0;
        return;
      }
      node = node.parentElement;
    }
    window.scrollTo(0, 0);
  }, [openFolder, openBudget]);

  const saveNote = (draft) => {
    const notes = local.notes || [];
    const exists = draft.id && notes.some((n) => n.id === draft.id);
    const record = exists
      ? draft
      : {
          ...draft,
          id: 'note_' + Math.random().toString(36).slice(2, 10),
          archived: false,
          createdAt: todayKey,
          year: taxYear,
          folderId: (noteSheet && noteSheet.folderId) || null,
        };
    persist({
      ...local,
      notes: exists ? notes.map((n) => (n.id === record.id ? record : n)) : [record, ...notes],
    });
    setNoteSheet(null);
  };

  // Every reminder change flows through persist(), so watching the array
  // covers save, edit, delete, tick and untick without wiring five call sites.
  // Running on mount also restores the queue after a reinstall, where iOS has
  // nothing scheduled but the reminders are still on the server.
  useEffect(() => {
    // Switched off means an empty queue, not a stale one - passing [] clears
    // everything we scheduled rather than leaving old alarms to fire.
    syncReminderNotifications(remindersEnabled ? (local.reminders || []) : []).catch(() => {});
  }, [local.reminders, remindersEnabled]);


  const pinnedSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  /**
   * Reorder pinned notes by moving them within plans.notes itself - the rows
   * render in array order, so that is the whole job, and no order field has to
   * be kept in step. Notes that are not pinned keep their positions.
   */
  const onPinnedDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const visible = showAllPinned ? pinned : pinned.slice(0, 2);
    const from = visible.findIndex((n) => n.id === active.id);
    const to = visible.findIndex((n) => n.id === over.id);
    if (from < 0 || to < 0) return;

    const reordered = arrayMove(pinned, from, to);
    const queue = [...reordered];
    const all = local.notes || [];
    // Walk the full list and drop the reordered pinned notes back into the
    // slots the pinned ones already occupied.
    const next = all.map((n) => (
      (n.kind === 'pinned' && thisYear(n) && !n.archived) ? queue.shift() : n
    ));

    persist({ ...local, notes: next });
  };

  const saveReminder = (draft) => {
    const list = local.reminders || [];
    const exists = draft.id && list.some((r) => r.id === draft.id);
    // Ask on the first reminder, with the context of having just written one.
    // Asking on tab open would prompt people who came to read a note.
    if (!exists && list.length === 0 && remindersEnabled) {
      ensurePermission().catch(() => {});
    }
    const record = exists
      ? draft
      : { ...draft, id: 'rem_' + Math.random().toString(36).slice(2, 10), done: false, doneAt: null };
    persist({
      ...local,
      reminders: exists ? list.map((r) => (r.id === record.id ? record : r)) : [...list, record],
    });
    setReminderSheet(null);
  };

  const toggleReminder = (id) => {
    persist({
      ...local,
      reminders: (local.reminders || []).map((r) => {
        if (r.id !== id) return r;
        // A repeat is never finished, so there is nothing to move to Done -
        // ticking it means "handled this one" and it moves to the next.
        if (!r.done && isRepeating(r)) {
          const next = advanceDue(r.dueAt, r.repeat, nowIso, r.repeatDay);
          // lastDoneAt rather than a tally: the question is whether it got done
          // last time round, not how many times it ever has.
          if (next) return { ...r, dueAt: next, done: false, doneAt: null, lastDoneAt: todayKey };
        }
        return { ...r, done: !r.done, doneAt: !r.done ? todayKey : null };
      }),
    });
  };

  const deleteReminder = (id) => {
    const gone = (local.reminders || []).find((r) => r.id === id);
    // Remove the Apple copy too - deleting here is deliberate, so the backup
    // should not outlive it.
    if (gone && gone.appleId) deleteAppleCopy(gone.appleId).catch(() => {});
    persist({ ...local, reminders: (local.reminders || []).filter((r) => r.id !== id) });
    setReminderSheet(null);
  };

  const appleSync = local.appleSync || {};

  /** Run a pass and write back only what actually changed. */
  const runAppleSync = async (remindersList, listId) => {
    // A second pass starting mid-flight would see reminders that have not had
    // their appleId persisted yet and create duplicates.
    if (appleRunRef.current.busy) return { listId, ids: {}, stats: {} };
    appleRunRef.current.busy = true;

    let res;
    try {
      // Fill in anything this session already exported but whose id has not
      // made it back into local state yet.
      const primed = (remindersList || []).map((r) => {
        if (r.appleId) return r;
        const known = appleRunRef.current.ids[r.id];
        return known ? { ...r, appleId: known.appleId, appleSig: known.sig } : r;
      });
      res = await syncApple(primed, listId);
    } finally {
      appleRunRef.current.busy = false;
    }

    console.log('[AppleReminders] pass:', JSON.stringify(res.stats || {}), 'list:', res.listId);
    if (!res.listId) return res;

    appleRunRef.current.ids = { ...appleRunRef.current.ids, ...res.ids };

    let changed = res.listId !== listId;
    const next = (remindersList || []).map((r) => {
      const hit = res.ids[r.id];
      if (!hit) return r;
      if (r.appleId === hit.appleId && r.appleSig === hit.sig) return r;
      changed = true;
      return { ...r, appleId: hit.appleId, appleSig: hit.sig };
    });

    // Persisting unconditionally would retrigger the effect that called this
    // and spin. Only write when something genuinely differs.
    if (changed) {
      persist({
        ...local,
        reminders: next,
        appleSync: { ...appleSync, enabled: true, listId: res.listId },
      });
    }
    return res;
  };

  const enableAppleBackup = async () => {
    setAppleBusy(true);
    try {
      const state = await requestAppleAccess();
      if (state !== 'granted') {
        setAppleBusy(false);
        setAppleSheet(false);
        return;
      }
      // recreateMissing: switching on means "make the list complete", so any
      // copy deleted in the Reminders app (or lost with a deleted list) is
      // put back. The busy flag stops the background pass overlapping it.
      let res;
      appleRunRef.current.busy = true;
      try {
        res = await syncApple(local.reminders || [], appleSync.listId, { recreateMissing: true });
      } finally {
        appleRunRef.current.busy = false;
      }
      appleRunRef.current.ids = { ...appleRunRef.current.ids, ...(res.ids || {}) };
      // Write the flag here rather than leaning on runAppleSync, which only
      // persists when a reminder changed. Re-enabling after switching off
      // changes nothing at all, so the flag would never have been saved.
      const next = (local.reminders || []).map((r) => {
        const hit = res.ids && res.ids[r.id];
        return hit ? { ...r, appleId: hit.appleId, appleSig: hit.sig } : r;
      });
      persist({
        ...local,
        reminders: next,
        appleSync: { ...appleSync, enabled: true, listId: res.listId || appleSync.listId },
      });
      setAppleSheet(false);
    } finally {
      setAppleBusy(false);
    }
  };

  const disableAppleBackup = () => {
    // Copies are left in place on purpose - they are the backup.
    persist({ ...local, appleSync: { ...appleSync, enabled: false } });
  };

  // Apple Reminders backup. Runs on mount and whenever reminders change; the
  // pass is repair-only, so an unchanged list writes nothing.
  useEffect(() => {
    if (!appleSync.enabled) return;
    runAppleSync(local.reminders || [], appleSync.listId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local.reminders, appleSync.enabled]);

  const saveFolder = (draft) => {
    const folders = local.folders || [];
    const exists = draft.id && folders.some((f) => f.id === draft.id);
    const record = exists
      ? draft
      : { ...draft, id: 'fold_' + Math.random().toString(36).slice(2, 10) };
    persist({
      ...local,
      folders: exists ? folders.map((f) => (f.id === record.id ? record : f)) : [...folders, record],
    });
    setFolderSheet(null);
  };

  // Deleting a folder unfiles its notes rather than taking them with it.
  const deleteFolder = (id) => {
    persist({
      ...local,
      folders: (local.folders || []).filter((f) => f.id !== id),
      notes: (local.notes || []).map((n) => (n.folderId === id ? { ...n, folderId: null } : n)),
    });
    setFolderSheet(null);
  };

  const moveNote = (noteId, folderId) => {
    persist({
      ...local,
      notes: (local.notes || []).map((n) => (n.id === noteId ? { ...n, folderId } : n)),
    });
    setMoveSheet(null);
  };

  const archiveNote = (id, archived) => {
    persist({
      ...local,
      notes: (local.notes || []).map((n) => (n.id === id ? { ...n, archived } : n)),
    });
    setNoteSheet(null);
  };

  const deleteNote = (id) => {
    persist({ ...local, notes: (local.notes || []).filter((n) => n.id !== id) });
    setNoteSheet(null);
  };

  /**
   * Copy last year's active notes into this year. New ids and a new year, so
   * the originals stay where they are - this duplicates rather than moves.
   */
  const bringForward = (ids) => {
    const prior = (local.notes || []).filter((n) => ids.includes(n.id));
    if (prior.length === 0) { setCopySheet(false); return; }
    // Copies, never moves - last year's notes stay where they are.
    const copies = prior.map((n) => ({
      ...n,
      id: 'note_' + Math.random().toString(36).slice(2, 10),
      year: taxYear,
      archived: false,
      createdAt: todayKey,
    }));
    persist({ ...local, notes: [...copies, ...(local.notes || [])] });
    setCopySheet(false);
  };

  const all = local.notes || [];
  const thisYear = (n) => (n.year == null
    ? taxYear === new Date().getFullYear()
    : n.year === taxYear);
  const archivedNotes = all.filter((n) => n.archived && thisYear(n));

  // Reminders belong to the year their due date falls in, so the year arrows
  // keep working without a second year field that could drift out of step.
  const nowIso = nowLocalIso();
  // Deliberately NOT filtered by taxYear. A note belongs to a tax year; a
  // reminder is a point in time, and scoping it to a year hid next year's
  // until you went looking for them.
  const horizon = new Date();
  horizon.setDate(horizon.getDate() + 90);
  const horizonKey = todayLocalKey(horizon);
  // plan_27: your money plan check-up, as a reminder (Money plan theme only).
  // Due on the 1st of the month a year after your last check-up, or later if
  // you pushed it back. It isn't saved as a reminder: it's worked out from
  // your plan each time, so finishing a check-up moves it on by itself.
  const checkupRem = (() => {
    if (!plan || !moneyPlanShown || !moneyPlan || !moneyPlan.finished) return null; // w2_01
    const cks = Array.isArray(moneyPlan.checkups) ? moneyPlan.checkups : [];
    const last = new Date(cks.length ? cks[cks.length - 1].date : (moneyPlan.finishedAt || moneyPlan.createdAt));
    if (isNaN(last)) return null;
    let due = new Date(last.getFullYear() + 1, last.getMonth(), 1, 12);
    if (moneyPlan.checkupSnooze) {
      const s = new Date(moneyPlan.checkupSnooze + 'T12:00:00');
      if (!isNaN(s) && s > due) due = s;
    }
    const key = due.getFullYear() + '-' + String(due.getMonth() + 1).padStart(2, '0') + '-' + String(due.getDate()).padStart(2, '0');
    return { id: 'mp-checkup', checkup: true, emoji: '\u{1F5FA}\u{FE0F}', title: 'Money plan check-up', dueAt: key + 'T12:00', last };
  })();
  const allReminders = (local.reminders || []).concat(checkupRem ? [checkupRem] : [])
    .filter((r) => r.dueAt)
    .sort((a, b) => (a.dueAt < b.dueAt ? -1 : 1));
  const overdue = allReminders.filter((r) => !r.done && r.dueAt < nowIso && r.dueAt.slice(0, 10) !== todayKey);
  const dueToday = allReminders.filter((r) => !r.done && r.dueAt.slice(0, 10) === todayKey);
  // Overdue and Today are left alone: they take repeats like anything else,
  // because a group that quietly omits things is worse than a longer one.
  // Everywhere else a repeat is held back and listed under Repeating instead,
  // so Soon and Later stay a list of one-off things to do.
  const soon = allReminders.filter((r) => !r.done && !isRepeating(r)
    && r.dueAt.slice(0, 10) > todayKey && r.dueAt.slice(0, 10) <= horizonKey);
  const later = allReminders.filter((r) => !r.done && !isRepeating(r)
    && r.dueAt.slice(0, 10) > horizonKey);
  const repeating = allReminders.filter((r) => !r.done && isRepeating(r)
    && r.dueAt.slice(0, 10) > todayKey);
  const doneReminders = allReminders.filter((r) => r.done);
  const visible = all.filter((n) => thisYear(n)
    && !n.archived);
  const pinned = visible.filter((n) => n.kind === 'pinned');
  const longs = visible.filter((n) => n.kind !== 'pinned');
  // Folders are not year-scoped - only the notes inside them are - so the same
  // folders are there every year without being recreated.
  const folders = local.folders || [];
  const folderIds = folders.map((f) => f.id);
  const unfiled = longs.filter((n) => !n.folderId || !folderIds.includes(n.folderId));
  const openFolderObj = folders.find((f) => f.id === openFolder) || null;
  // The FAB must never float over an open sheet.
  const anySheetOpen = !!(noteSheet || reminderSheet || folderSheet || moveSheet
    || copySheet || showInfo || showAddMenu);
  const openFolderNotes = openFolderObj ? longs.filter((n) => n.folderId === openFolderObj.id) : [];
  const priorCount = all.filter((n) => n.year === taxYear - 1 && !n.archived).length;

  const Eyebrow = ({ children, className = '' }) => (
    <p
      className={"text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2 " + className}
      style={{ color: eyebrow }}
    >
      {children}
    </p>
  );

  // Proportioned from the reference: the tab is about 14% of the total height
  // and 45% of the width, with a diagonal inner edge rather than a rounded
  // step. The earlier version had a tab twice this tall, which is what made it
  // look top-heavy.
  const FOLDER_TAB = "M4 10a5 5 0 0 1 5-5h16l6 6v5H4V10z";
  const FOLDER_BODY = "M4 16a5 5 0 0 1 5-5h46a5 5 0 0 1 5 5v26a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V16z";

  const FolderShape = ({ tone, deep }) => (
    <svg viewBox="0 0 64 50" style={{ width: '100%', display: 'block' }}>
      <path d={FOLDER_TAB} fill={deep} />
      <path d={FOLDER_BODY} fill={tone} />
    </svg>
  );

  // The light hero-box gradient is the folder colour; the tab uses the deeper
  // stop of the same gradient so it reads as one colour with depth.
  // Derived from the live palette rather than fixed, so a folder picked on one
  // theme takes the equivalent colour on another. The deeper gradient stop is
  // the folder body; the tab is that stop darkened slightly.
  const FOLDER_FILLS = tilePalette.map((t) => {
    const stops = paletteStops(t.bg);
    const body = stops[1] || stops[0];
    return { tone: body, deep: shadeHex(body, 0.9) };
  });

  const FolderTile = ({ folder }) => {
    const fill = FOLDER_FILLS[folder.color || 0];
    const count = longs.filter((n) => n.folderId === folder.id).length;
    if (plan) {
      const t = tilePalette[(folder.color || 0) % tilePalette.length];
      return (
        <button onClick={() => setOpenFolder(folder.id)} className="text-left" style={{ background: '#FFFFFF', borderRadius: '22px', padding: '16px' }}>
          <span style={{ width: '44px', height: '44px', borderRadius: '14px', background: t.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>{'\ud83d\uddc2\ufe0f'}</span>
          <span className="block truncate" style={{ fontSize: '15px', fontWeight: 600, color: '#26252E', marginTop: '12px' }}>{folder.name}</span>
          <span className="block" style={{ fontSize: '12.5px', color: '#9E9AAB', marginTop: '2px' }}>{count} note{count === 1 ? '' : 's'}</span>
        </button>
      );
    }
    return (
      <button onClick={() => setOpenFolder(folder.id)} className="flex flex-col items-center">
        <span className="block w-full px-1.5">
          <FolderShape tone={fill.tone} deep={fill.deep} />
        </span>
        <span className="block text-[12.5px] font-semibold mt-2 text-center leading-tight w-full truncate" style={{ color: '#1F2937' }}>
          {folder.name}
        </span>
        <span className="block text-[11px] text-gray-400 mt-0.5">
          {count} note{count === 1 ? '' : 's'}
        </span>
      </button>
    );
  };

  // Fixed square regardless of title length, so a row never stretches to match
  // its tallest tile. An over-length legacy title clamps rather than breaking
  // the grid, which is why nothing had to be migrated.
  const NoteTile = ({ n }) => (
    <div className={cardCls} style={{ position: 'relative', aspectRatio: '4 / 3' }}>
      <button
        onClick={() => setNoteSheet({ note: n, kind: n.kind, mode: 'read' })}
        className="absolute inset-0 w-full h-full flex flex-col text-left"
        style={{ padding: '13px 13px 12px' }}
      >
        <span
          className="flex-1 min-w-0 text-[14px] font-semibold"
          style={{
            color: '#1F2937',
            lineHeight: 1.32,
            paddingRight: '24px',
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {n.title || 'Untitled'}
        </span>
        <span className="text-[11.5px] flex-shrink-0" style={{ color: '#a39fa0' }}>
          {n.createdAt ? prettyDate(n.createdAt) : ''}
        </span>
      </button>
      <button
        onClick={() => setMoveSheet({ note: n })}
        className="absolute flex items-center justify-center"
        style={{ top: '8px', right: '8px', width: '26px', height: '26px', borderRadius: '50%', background: track }}
      >
        <MoreHorizontal className="w-3.5 h-3.5 text-gray-500" />
      </button>
    </div>
  );

  const NoteRow = ({ n, first }) => {
    const tone = n.tag ? tagTone(tilePalette, n.tag) : null;
    // Same sensor setup as the spending rules: an 8px activation distance, so
    // a tap still opens the note and only a deliberate drag reorders.
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: n.id });
    return (
      <button
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          opacity: isDragging ? 0.5 : 1,
          touchAction: 'none',
        }}
        onClick={() => setNoteSheet({ note: n, kind: n.kind, mode: 'read' })}
        className={'w-full flex gap-3 items-center py-3.5 text-left'
          + (first ? '' : ' border-t theme-border-100')}
      >
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ background: tone ? tone.fg : '#D9D2D6' }}
        />
        <span
          className="flex-1 min-w-0 text-[14.5px] font-semibold"
          style={{
            color: '#1F2937',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {n.title || 'Untitled'}
        </span>
        {tone && (
          <span
            className="text-[11px] font-bold px-2.5 py-1 rounded-full flex-shrink-0"
            style={{ background: tone.bg, color: tone.fg }}
          >
            {n.tag}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="px-4 pt-3" ref={rootRef}>
      {/* Tailwind's preflight strips list styling app-wide, so put it back for
          note content only. Covers the editor, the reader and the sheets. */}
      <style>{`
        .worthly-rich ul { list-style: disc; padding-left: 1.15em; margin: 6px 0; }
        .worthly-rich li { margin-bottom: 3px; }
        .worthly-tool { transition: transform .12s ease, background-color .12s ease, box-shadow .12s ease; }
        .worthly-tool:active { transform: scale(0.88); }
      `}</style>
      {budgetOpen ? null : openFolderObj && !plan ? (
        <div className="flex items-center gap-2.5 px-1 pt-3 wy-ph" style={{ '--ph-lift': '0.75rem', '--ph-pad': '0.75rem', '--ph-inset': '0.25rem' }}>
          <button
            onClick={() => setOpenFolder(null)}
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: track }}
          >
            <ChevronLeft className="w-4 h-4" style={{ color: accent }} />
          </button>
          <h1 className="m-0 flex-1 min-w-0 truncate" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '26px', fontWeight: 400, color: '#1A1A1F', letterSpacing: '-0.6px' }}>
            {openFolderObj.name}
          </h1>
          <button
            onClick={() => setFolderSheet({ folder: openFolderObj })}
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: track }}
          >
            <MoreHorizontal className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      ) : (
      <div className="flex items-center justify-between px-1 pt-3 wy-ph" style={{ '--ph-lift': '0.75rem', '--ph-pad': '0.75rem', '--ph-inset': '0.25rem' }}>
        <h1 className="m-0 flex items-center" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '24px', fontWeight: 400, color: '#1A1A1F', letterSpacing: '-0.6px' }}>
          {onBack && (
            <button
              onClick={onBack}
              aria-label="Back to Home"
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: track, marginRight: '10px' }}
            >
              <ChevronLeft className="w-4 h-4" style={{ color: accent }} />
            </button>
          )}
          {plan ? (tab === 'budgets' ? 'budget sheets' : tab === 'reminders' ? 'reminders' : 'notes') : (tab === 'budgets' ? 'Budget Sheets' : tab === 'reminders' ? 'Reminders' : 'Notes')}
          <button
            onClick={() => (tab === 'budgets' ? setBudgetInfo(true) : setShowInfo(true))}
            style={{ background: '#F4F1F8', border: '1px solid #EFEAF1', cursor: 'pointer', borderRadius: '50%', width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#6B6B73', padding: 0, flexShrink: 0, marginLeft: '8px', fontFamily: "'DM Sans', sans-serif" }}
          >i</button>
        </h1>
        {/* Reminders span every year, so the arrows would do nothing there. */}
        <div className="flex items-center gap-2 flex-shrink-0" style={{ color: muted, visibility: tab === 'notes' ? 'visible' : 'hidden' }}>
          <button onClick={() => setTaxYear((y) => y - 1)} className="w-4 h-4 flex items-center justify-center">
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <span className="text-[15px] font-medium">{taxYear}</span>
          <button onClick={() => setTaxYear((y) => y + 1)} className="w-4 h-4 flex items-center justify-center">
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      )}

      {openFolderObj && !plan ? (
        <div className="mt-5">
          <div className="grid grid-cols-2 gap-3">
            {openFolderNotes.map((n) => <NoteTile key={n.id} n={n} />)}
          </div>
          <div style={{ height: '8px' }} />
        </div>
      ) : (
      <div className="mt-5">
        {/* Same tab bar as the Contributions Planner - these two pages sit one
            tap apart on Home, so they should not disagree about what a tab bar
            looks like. */}
        {!budgetOpen && !section && (
        <div className="flex justify-center mt-4 mb-5 wy-tabs" style={{ gap: '48px' }}>
          {[['notes', 'Notes'], ['reminders', 'Reminders'], ['budgets', 'Budgets']].map(([k, lab]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-on={tab === k ? '1' : undefined}
              className="relative pb-2 text-[15px]"
              style={{
                color: tab === k ? '#1F2937' : muted,
                fontWeight: tab === k ? 700 : 500,
              }}
            >
              {lab}
              {tab === k && (
                <span
                  className="absolute left-1/2 bottom-0 h-[3px] rounded-full"
                  style={{ width: '34px', transform: 'translateX(-50%)', background: accent }}
                />
              )}
            </button>
          ))}
        </div>
        )}

        {tab === 'budgets' ? (
          <BudgetSheets
            sheets={local.budgetSheets || []}
            onChange={(next) => persist({ ...local, budgetSheets: next })}
            openId={openBudget}
            onOpen={setOpenBudget}
            theme={theme}
            accent={accent}
            track={track}
            eyebrow={eyebrow}
            muted={muted}
            palette={tilePalette}
            cardCls={cardCls}
            infoOpen={budgetInfo}
            onInfoChange={setBudgetInfo}
          />
        ) : tab === 'reminders' ? (
          <>
            {(() => {
              // Calmer reminders (purple_01, every theme): white cards with
              // dark text. Colour only shows in a small dot by each section
              // name and in the soft date pill. Overdue stays red on every
              // theme - it is an alarm rather than decoration.
              const PILLS = plan ? ({
                soon: ['#F5F2FA', '#6C6879'], later: ['#F5F2FA', '#6C6879'], repeat: ['#F5F2FA', '#6C6879'],
              }) : purple ? ({
                soon: ['#EFE6FC', '#6E43C8'], later: ['#F3F1F7', '#7D7590'], repeat: ['#E8DEFA', '#5E35A8'],
              }) : ({
                pink: { soon: ['#FBE6F0', '#B12F70'], later: ['#E7F1DB', '#4F7A3E'], repeat: ['#EEE6FA', '#6E43B5'] },
                warm: { soon: ['#E4EEE2', '#2D5A3D'], later: ['#EFEDE8', '#6F6A60'], repeat: ['#F5E8DA', '#9B5A22'] },
                blue: { soon: ['#E3E8FB', '#3A4DA8'], later: ['#EEF0F3', '#646B80'], repeat: ['#ECE6FA', '#6B4FB0'] },
              }[isPink ? 'pink' : isWarm ? 'warm' : 'blue']);
              const tone = ([bg, fg], dot) => ({ bg, fg, dot: dot || fg });
              const RED = plan ? tone(['#FAE4ED', '#C04B7C']) : tone(['#FFE4E9', '#E11D48']);
              const soonTone = tone(PILLS.soon);
              const laterTone = tone(PILLS.later, isPink && !purple ? '#8DBB75' : '#B3ACBD');
              const repeatTone = tone(PILLS.repeat);
              const doneTone = tone(['#F3F1F4', '#a39fa0'], '#CFC9D4');
              const INK = '#1F2937';
              const LINE = '1px solid rgba(31,22,48,0.07)';
              const CARD = 'bg-white rounded-2xl shadow-sm px-4';
              const CARD_STYLE = { paddingTop: '2px', paddingBottom: '2px', border: '1px solid rgba(31,22,48,0.04)' };

              const groups = [
                { key: 'overdue', label: 'Overdue', items: overdue, tone: RED },
                { key: 'today', label: 'Today', items: dueToday, tone: soonTone },
                { key: 'soon', label: 'Soon', items: soon, tone: soonTone },
              ].filter((g) => g.items.length > 0);

              // plan_15: Money plan look (option A).
              if (plan) {
                const T = PLAN_REM_TONES;
                const when = (r, kind) => {
                  const k = r.dueAt.slice(0, 10);
                  const d = planDayDiff(todayKey, k);
                  if (kind === 'overdue') return d >= 0 ? 'Earlier today' : d === -1 ? 'Yesterday' : (-d) + ' days ago';
                  if (kind === 'today') return 'Today';
                  if (d === 1) return 'Tomorrow';
                  if (d < 7) return new Date(k + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' });
                  if (d < 28) return 'In ' + d + ' days';
                  if (d < 60) return 'In a month';
                  return 'In ' + Math.round(d / 30) + ' months';
                };
                const PRow = ({ r, kind }) => {
                  const t = T[r.done ? 'done' : kind];
                  const k = r.dueAt.slice(0, 10);
                  const tp = kind === 'today' && !r.done ? planTimeParts(r.dueAt) : null;
                  const extra = [
                    isRepeating(r) && repeatLabel(r.repeat) ? '\u{1F501} ' + repeatLabel(r.repeat) : null,
                    r.note ? '\u{1F4DD} Has a note' : null,
                    r.link ? '\u{1F517} Has a link' : null,
                  ].filter(Boolean);
                  return (
                    <div className="flex items-center" style={{ gap: 13, padding: '12px 0' }}>
                      <button type="button" onClick={() => (r.checkup ? setCheckupSheet(true) : setReminderSheet({ reminder: r, mode: 'read' }))} className="flex items-center flex-1 min-w-0 text-left" style={{ gap: 13, background: 'none', border: 0, padding: 0 }}>
                        <span className="flex flex-col items-center justify-center flex-shrink-0" style={{ width: 46, height: 46, borderRadius: 15, background: t.bg, color: t.ink, lineHeight: 1 }}>
                          <b style={{ fontSize: tp && tp.big.length > 2 ? 14 : 17, fontWeight: 600 }}>{tp ? tp.big : Number(k.slice(8, 10))}</b>
                          <small style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', marginTop: 3, textTransform: 'uppercase' }}>{tp ? tp.small : PLAN_REM_MONTHS[Number(k.slice(5, 7)) - 1]}</small>
                        </span>
                        <span className="flex-1 min-w-0">
                          <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: 15, fontWeight: 500, lineHeight: 1.3, color: r.done ? '#A39FA0' : '#33343A', textDecoration: r.done ? 'line-through' : 'none' }}>{r.emoji ? r.emoji + ' ' : ''}{r.title}</span>
                          <span style={{ display: 'block', fontSize: 12.5, color: '#8B879A', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r.done ? 'Done' + (r.doneAt ? ' · ' + prettyShort(r.doneAt) : '') : [when(r, kind)].concat(extra).join(' · ')}
                          </span>
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => (r.checkup ? setCheckupSheet(true) : toggleReminder(r.id))}
                        aria-label={r.done ? 'Mark not done' : 'Mark done'}
                        className="flex items-center justify-center flex-shrink-0"
                        style={{ width: 26, height: 26, borderRadius: 999, border: '2px solid ' + t.ring, background: r.done ? '#9ED9B6' : 'transparent', padding: 0 }}
                      >
                        {r.done && <Check className="w-3.5 h-3.5" style={{ color: '#fff' }} strokeWidth={3} />}
                      </button>
                    </div>
                  );
                };
                const PList = ({ items, kind }) => (
                  <div style={{ background: '#FFFFFF', borderRadius: 28, padding: '4px 18px' }}>
                    {items.map((r, i) => (
                      <div key={r.id} style={i ? { borderTop: '1px solid #EEEAF6' } : undefined}><PRow r={r} kind={kind} /></div>
                    ))}
                  </div>
                );
                const Head = ({ text, n, tone, color, open, onToggle }) => {
                  const inner = (
                    <>
                      <span style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.3px', color: color || '#2B2C31' }}>{text}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, borderRadius: 999, padding: '3px 9px', background: tone.bg, color: tone.ink }}>{n}</span>
                      {onToggle && <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: '#B9B5C6', marginLeft: 'auto' }} />}
                    </>
                  );
                  return onToggle ? (
                    <button type="button" onClick={onToggle} className="w-full flex items-center" style={{ gap: 8, margin: '26px 0 10px', padding: '0 4px', background: 'none', border: 0 }}>{inner}</button>
                  ) : (
                    <div className="flex items-center" style={{ gap: 8, margin: '26px 4px 10px' }}>{inner}</div>
                  );
                };
                // The week strip: today and the next six days, with a dot for
                // each reminder due that day.
                const open = allReminders.filter((r) => !r.done);
                const days = Array.from({ length: 7 }, (_, i) => {
                  const d = new Date(todayKey + 'T12:00:00');
                  d.setDate(d.getDate() + i);
                  const key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
                  const items = open.filter((r) => r.dueAt.slice(0, 10) === key);
                  return { key, d, items, i };
                });
                const nToday = dueToday.length;
                const nLate = overdue.length;
                const weekLine = nToday || nLate
                  ? [nToday ? nToday + ' today' : null, nLate ? nLate + ' overdue' : null].filter(Boolean).join(' and ') + '.'
                  : open.length ? 'Nothing due today.' : 'All clear!';
                const nothing = groups.length === 0 && later.length === 0 && repeating.length === 0 && doneReminders.length === 0;
                return (
                  <>
                    <div style={{ background: '#FFFFFF', borderRadius: 28, padding: '20px 18px 16px' }} data-testid="plan-rem-week">
                      <div className="flex items-start justify-between" style={{ gap: 12 }}>
                        <div style={{ minWidth: 0 }}>
                          <h3 style={{ margin: 0, fontSize: 19, fontWeight: 600, letterSpacing: '-0.3px', color: '#2B2C31' }}>This week {'\u{1F5D3}️'}</h3>
                          <p style={{ margin: '5px 0 0', fontSize: 13.5, color: '#7A7A82', lineHeight: 1.45 }}>
                            {nothing ? 'Tap + to add a reminder and you’ll get a notification when it’s due.' : weekLine}
                          </p>
                        </div>
                        <span className="flex items-center justify-center flex-shrink-0" style={{ width: 50, height: 50, borderRadius: 999, background: '#ECE6FA', fontSize: 23 }}>{'\u{1F514}'}</span>
                      </div>
                      <div className="flex justify-between" style={{ marginTop: 18 }}>
                        {days.map((day) => (
                          <div key={day.key} style={{ flex: 1, textAlign: 'center' }}>
                            <div style={{ fontSize: 11.5, color: '#A9A4B8', fontWeight: 500 }}>{day.d.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                            <div className="flex items-center justify-center" style={{ width: 38, height: 38, margin: '6px auto 0', borderRadius: 999, fontSize: 14.5, fontWeight: 600, background: day.i === 0 ? '#1F1E24' : '#F7F5FB', color: day.i === 0 ? '#FFFFFF' : '#55565C' }}>{day.d.getDate()}</div>
                            <div className="flex justify-center" style={{ gap: 3, height: 6, marginTop: 6 }}>
                              {day.items.slice(0, 3).map((r) => (
                                <i key={r.id} style={{ width: 5, height: 5, borderRadius: 9, display: 'block', background: day.i === 0 ? '#A58BE6' : isRepeating(r) ? '#7CC79C' : '#7FA9E4' }} />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {overdue.length > 0 && (<><Head text="Overdue" n={overdue.length} tone={T.overdue} color={T.overdue.ink} /><PList items={overdue} kind="overdue" /></>)}
                    {dueToday.length > 0 && (<><Head text="Today" n={dueToday.length} tone={T.today} /><PList items={dueToday} kind="today" /></>)}
                    {soon.length > 0 && (<><Head text="Soon" n={soon.length} tone={T.soon} /><PList items={soon} kind="soon" /></>)}

                    {repeating.length > 0 && (
                      <>
                        <Head text="Repeating" n={repeating.length} tone={{ bg: '#DFF2E7', ink: '#2F8756' }} />
                        <div className="grid grid-cols-2" style={{ gap: 10 }}>
                          {repeating.map((r, i) => {
                            const [bg, ink] = PLAN_REPEAT_TONES[i % PLAN_REPEAT_TONES.length];
                            return (
                              <button key={r.id} type="button" onClick={() => setReminderSheet({ reminder: r, mode: 'read' })} className="text-left" style={{ background: bg, borderRadius: 22, padding: 14, border: 0, minWidth: 0 }}>
                                <span style={{ fontSize: 20, display: 'block' }}>{r.emoji || planRepeatEmoji(r.title)}</span>
                                <span style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: '#2B2C31', marginTop: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</span>
                                <span style={{ display: 'block', fontSize: 12.5, color: ink, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{[repeatLabel(r.repeat), prettyShort(r.dueAt.slice(0, 10))].filter(Boolean).join(' · ')}</span>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {later.length > 0 && (
                      <>
                        <Head text="Later" n={later.length} tone={T.later} open={showLater} onToggle={() => setShowLater(!showLater)} />
                        {showLater && <PList items={later} kind="later" />}
                      </>
                    )}

                    {/* plan_27: the check-up sheet */}
                    {checkupSheet && checkupRem && createPortal(
                      <div className="fixed inset-0" style={{ zIndex: 70 }} data-patch="plan_27">
                        <div className="absolute" style={{ inset: '-60px 0', background: 'rgba(0,0,0,0.4)' }} onClick={() => setCheckupSheet(false)} />
                        <div className="absolute bottom-0 left-0 right-0" style={{ background: '#FFFFFF', borderRadius: '28px 28px 0 0', padding: '22px 18px calc(env(safe-area-inset-bottom, 0px) + 24px)', boxShadow: '0 -8px 30px rgba(40,30,80,0.10)' }}>
                          <span className="flex items-center justify-center" style={{ width: 64, height: 64, borderRadius: 22, background: '#ECE6FA', fontSize: 30 }}>{'\u{1F5FA}\u{FE0F}'}</span>
                          <h3 style={{ margin: '14px 0 4px', fontSize: 21, fontWeight: 600, letterSpacing: '-0.3px', color: '#2B2C31' }}>Money plan check-up</h3>
                          <p style={{ margin: 0, fontSize: 14, color: '#6C6879', lineHeight: 1.5 }}>Once a year, you look at your plan again: your pay, your goals and how they're doing. It takes about 15 minutes.</p>
                          <div className="flex" style={{ gap: 8, marginTop: 14 }}>
                            <div style={{ flex: 1, background: '#F7F5FB', borderRadius: 16, padding: '10px 12px' }}><span style={{ display: 'block', fontSize: 11.5, color: '#8B879A' }}>Last one</span><b style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: '#2B2C31', marginTop: 2 }}>{checkupRem.last.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</b></div>
                            <div style={{ flex: 1, background: '#F7F5FB', borderRadius: 16, padding: '10px 12px' }}><span style={{ display: 'block', fontSize: 11.5, color: '#8B879A' }}>Due</span><b style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: '#2B2C31', marginTop: 2 }}>{checkupRem.dueAt.slice(0, 10) <= todayKey ? 'Now' : new Date(checkupRem.dueAt.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</b></div>
                          </div>
                          <button type="button" onClick={() => { setCheckupSheet(false); if (onOpenCheckup) onOpenCheckup(); }} className="w-full" style={{ marginTop: 16, height: 52, border: 0, borderRadius: 999, background: '#1F1E24', color: '#FFFFFF', fontSize: 15, fontWeight: 600 }}>Start my check-up</button>
                          <button
                            type="button"
                            onClick={() => {
                              const d = new Date(todayKey + 'T12:00:00');
                              d.setDate(d.getDate() + 7);
                              const k = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
                              saveUserData({ 'money_diary.plan.checkupSnooze': k });
                              setCheckupSheet(false);
                            }}
                            className="w-full"
                            style={{ marginTop: 8, height: 50, border: 0, borderRadius: 999, background: '#F7F5FB', color: '#55565C', fontSize: 14.5, fontWeight: 600 }}
                          >Remind me next week</button>
                        </div>
                      </div>,
                      document.body
                    )}

                    {doneReminders.length > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <button type="button" onClick={() => setShowDone(!showDone)} className="w-full flex items-center justify-center gap-1.5 py-3" style={{ fontSize: 14, fontWeight: 500, color: '#8B879A', background: 'none', border: 0 }}>
                          Done ({doneReminders.length})
                          <ChevronDown className={`w-4 h-4 transition-transform ${showDone ? 'rotate-180' : ''}`} />
                        </button>
                        {showDone && <PList items={doneReminders} kind="done" />}
                      </div>
                    )}
                  </>
                );
              }

              const Label = ({ text, t, color }) => plan ? (
                <span style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '-0.4px', color: '#26252E' }}>{text}</span>
              ) : (
                <span
                  className="flex items-center text-[12px] font-bold uppercase tracking-[0.05em]"
                  style={{ color: color || eyebrow }}
                >
                  <span
                    className="inline-block rounded-full flex-shrink-0"
                    style={{ width: '7px', height: '7px', background: t.dot, marginRight: '7px' }}
                  />
                  {text}
                </span>
              );

              const Row = ({ r, tone: t, first }) => (
                <div
                  className="flex items-center gap-3 py-3"
                  style={first ? undefined : { borderTop: LINE }}
                >
                  <button
                    onClick={() => toggleReminder(r.id)}
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: '22px', height: '22px', borderRadius: '50%',
                      border: '2px solid ' + (r.done ? tilePalette[2].value : t === RED ? '#F2A3B5' : '#D5CCDD'),
                      background: r.done ? tilePalette[2].value : 'transparent',
                    }}
                  >
                    {r.done && <Check className="w-3 h-3" style={{ color: '#fff' }} strokeWidth={3} />}
                  </button>
                  <button
                    onClick={() => setReminderSheet({ reminder: r, mode: 'read' })}
                    className="flex-1 min-w-0 flex items-center gap-2 text-left"
                  >
                    <span className="flex-1 min-w-0">
                      <span
                        className="text-[14.5px]"
                        style={{
                          color: r.done ? '#a39fa0' : INK,
                          fontWeight: 500,
                          textDecoration: r.done ? 'line-through' : 'none',
                          lineHeight: 1.3,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {r.title}
                      </span>
                      {/* Without this a repeat sitting in Today is
                          indistinguishable from a one-off, and ticking it does
                          something different. */}
                      {isRepeating(r) && (
                        <span
                          className="flex items-center gap-1 text-[11.5px] font-medium"
                          style={{ color: '#8E879A', marginTop: '3px' }}
                        >
                          <Repeat className="w-3 h-3" /> {repeatLabel(r.repeat)}
                          {r.lastDoneAt ? ' · last done ' + prettyShort(r.lastDoneAt) : ''}
                        </span>
                      )}
                    </span>
                    {r.note && (
                      <span className="flex items-center justify-center flex-shrink-0" style={{ width: '22px', height: '22px', borderRadius: '7px', background: '#F5F3F6' }}>
                        <FileText className="w-3 h-3" style={{ color: '#9A93A6' }} />
                      </span>
                    )}
                    {r.link && (
                      <span className="flex items-center justify-center flex-shrink-0" style={{ width: '22px', height: '22px', borderRadius: '7px', background: '#F5F3F6' }}>
                        <Link2 className="w-3 h-3" style={{ color: '#9A93A6' }} />
                      </span>
                    )}
                    <span
                      className="text-[11.5px] font-bold flex-shrink-0"
                      style={{
                        background: r.done ? doneTone.bg : t.bg,
                        color: r.done ? doneTone.fg : t.fg,
                        padding: '4px 9px',
                        borderRadius: '999px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {prettyShort(r.dueAt.slice(0, 10))}
                    </span>
                  </button>
                </div>
              );

              return (
                <>
                  {groups.length === 0 && later.length === 0 && doneReminders.length === 0 && (
                    <div className="bg-white rounded-2xl shadow-sm p-6 text-center mb-4" style={{ border: '1px solid rgba(31,22,48,0.04)' }}>
                      <span
                        className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3"
                        style={{ background: track }}
                      >
                        <Bell className="w-5 h-5" style={{ color: eyebrow }} />
                      </span>
                      <p className="m-0 text-[13px] leading-relaxed text-gray-500">
                        Nothing scheduled yet. Tap + to add a reminder and you'll
                        get a notification when it's due.
                      </p>
                    </div>
                  )}

                  {groups.map((g) => (
                    <div key={g.key} className="mb-4">
                      <p className="m-0 mb-2 px-1">
                        <Label text={g.label} t={g.tone} color={g.tone === RED ? RED.fg : undefined} />
                      </p>
                      <div className={CARD} style={CARD_STYLE}>
                        {g.items.map((r, i) => <Row key={r.id} r={r} tone={g.tone} first={i === 0} />)}
                      </div>
                    </div>
                  ))}

                  {later.length > 0 && (
                    <div className="mb-4">
                      <button
                        onClick={() => setShowLater(!showLater)}
                        className="w-full flex items-center justify-between mb-2 px-1"
                      >
                        <Label text={`Later (${later.length})`} t={laterTone} />
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${showLater ? 'rotate-180' : ''}`}
                          style={{ color: eyebrow }}
                        />
                      </button>
                      {showLater && (
                        <div className={CARD} style={CARD_STYLE}>
                          {later.map((r, i) => <Row key={r.id} r={r} tone={laterTone} first={i === 0} />)}
                        </div>
                      )}
                    </div>
                  )}

                  {repeating.length > 0 && (
                    <div className="mb-4">
                      <button
                        onClick={() => setShowRepeating(!showRepeating)}
                        className="w-full flex items-center justify-between mb-2 px-1"
                      >
                        <Label text={`Repeating (${repeating.length})`} t={repeatTone} />
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${showRepeating ? 'rotate-180' : ''}`}
                          style={{ color: eyebrow }}
                        />
                      </button>
                      {showRepeating && (
                        <div className={CARD} style={CARD_STYLE}>
                          {repeating.map((r, i) => <Row key={r.id} r={r} tone={repeatTone} first={i === 0} />)}
                        </div>
                      )}
                    </div>
                  )}

                  {doneReminders.length > 0 && (
                    <div className="mt-4">
                      <button
                        onClick={() => setShowDone(!showDone)}
                        className="w-full flex items-center justify-center gap-1.5 py-3 text-[13px] font-semibold"
                        style={{ color: '#a39fa0' }}
                      >
                        Done ({doneReminders.length})
                        <ChevronDown className={`w-4 h-4 transition-transform ${showDone ? 'rotate-180' : ''}`} />
                      </button>
                      {showDone && (
                        <div className={CARD} style={CARD_STYLE}>
                          {doneReminders.map((r, i) => (
                            <Row key={r.id} r={r} tone={doneTone} first={i === 0} />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            {/* Backup to Apple Reminders. Sits here rather than in Settings:
                this is where someone thinking about reminders will look. */}
            <button
              onClick={() => (appleSync.enabled ? disableAppleBackup() : setAppleSheet(true))}
              className={cardCls + ' w-full p-4 mt-5 flex items-center gap-3 text-left'}
              data-testid="apple-backup-row"
            >
              <span
                className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
                style={{ background: plan ? '#FAE4ED' : track, borderRadius: plan ? 14 : undefined }}
              >
                {'\ud83c\udf4e'}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[14.5px] font-semibold" style={{ color: '#1F2937' }}>
                  {plan ? 'Back up to Apple Reminders' : 'Back Up to Apple Reminders'}
                </span>
                <span className="block text-[12px] text-gray-500 mt-0.5 leading-snug">
                  {appleSync.enabled
                    ? 'On \u00b7 your reminders are copied to a Worthly list'
                    : 'Keep a copy in your Reminders app'}
                </span>
              </span>
              <span
                className="flex-shrink-0 rounded-full relative"
                style={{
                  width: '44px', height: '24px',
                  background: appleSync.enabled ? (plan ? '#9ED9B6' : accent) : '#D9D2D6',
                  transition: 'background .15s ease',
                }}
              >
                <span
                  className="absolute bg-white rounded-full"
                  style={{
                    width: '20px', height: '20px', top: '2px',
                    left: appleSync.enabled ? '22px' : '2px',
                    transition: 'left .15s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,.15)',
                  }}
                />
              </span>
            </button>
          </>
        ) : (
          <>
            {visible.length === 0 && priorCount > 0 && (
              <button
                onClick={() => setCopySheet(true)}
                className={cardCls + ' w-full p-4 mb-4 flex items-center gap-3 text-left'}
              >
                <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: track }}>
                  <Copy className="w-4 h-4" style={{ color: accent }} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[14.5px] font-semibold" style={{ color: '#1F2937' }}>
                    Bring forward from {taxYear - 1}
                  </span>
                  <span className="block text-[12px] text-gray-500 mt-0.5 leading-snug">
                    Choose from {priorCount} note{priorCount === 1 ? '' : 's'} to copy into
                    {' '}{taxYear}. The originals stay where they are.
                  </span>
                </span>
              </button>
            )}

            {/* w2_11: the money timeline and Every payday live on Home only */}
            <div className="flex items-center justify-between mb-2 px-1">
              <p
                className="m-0 text-[13px] font-bold uppercase tracking-[0.08em]"
                style={{ color: eyebrow }}
              >
                {plan ? '' : '\ud83d\udccc '}Pinned
              </p>
            </div>
            <div className={cardCls + ' p-4'}>
              {pinned.length === 0 ? (
                <p className="m-0 text-[13px] text-gray-500 leading-snug">
                  Short rules and decisions you want to keep in view. Tap + to
                  add one.
                </p>
              ) : (
                <div style={{ marginTop: '-8px' }}>
                  <DndContext
                    sensors={pinnedSensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onPinnedDragEnd}
                  >
                    <SortableContext
                      items={(showAllPinned ? pinned : pinned.slice(0, 2)).map((n) => n.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {(showAllPinned ? pinned : pinned.slice(0, 2)).map((n, i) => (
                        <NoteRow key={n.id} n={n} first={i === 0} />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              )}
              {pinned.length > 2 && (
                <button
                  onClick={() => setShowAllPinned(!showAllPinned)}
                  className="w-full mt-2 pt-2.5 border-t theme-border-100 text-[12.5px] font-semibold flex items-center justify-center gap-1"
                  style={{ color: muted }}
                  data-testid="pinned-toggle-btn"
                >
                  {showAllPinned ? 'Show less' : `${pinned.length - 2} more`}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAllPinned ? 'rotate-180' : ''}`} />
                </button>
              )}
            </div>

            <div className="flex items-center justify-between mt-6 mb-2 px-1">
              <p
                className="m-0 text-[13px] font-bold uppercase tracking-[0.08em]"
                style={{ color: eyebrow }}
              >
                <span className="wy-star">✦ </span>Long notes
              </p>
            </div>

            {folders.length > 0 && (
              <div className={plan ? "grid grid-cols-2 gap-3 mb-5" : "grid grid-cols-3 gap-3 mb-5"}>
                {folders.map((f) => <FolderTile key={f.id} folder={f} />)}
              </div>
            )}

            {/* The label only earns its place when there are folders to
                distinguish these from, and the whole section goes when empty. */}
            {unfiled.length > 0 && folders.length > 0 && (
              <p className="m-0 text-[12px] font-semibold text-gray-400 mb-2.5 px-1">Unfiled</p>
            )}
            {unfiled.length > 0 && (
              <div className="grid grid-cols-2 gap-3">
                {unfiled.map((n) => <NoteTile key={n.id} n={n} />)}
              </div>
            )}
            {unfiled.length === 0 && folders.length === 0 && (
              <p className="m-0 text-[13px] text-gray-500 leading-snug px-1">
                Nothing here yet. Tap + to start one.
              </p>
            )}

            {/* Archived is a graveyard, not a view. It gets a disclosure at the
                foot of the page rather than a permanent tab. */}
            {archivedNotes.length > 0 && (
              <div className="mt-6">
                <button
                  onClick={() => setShowArchived(!showArchived)}
                  className="w-full flex items-center justify-center gap-1.5 py-3 text-[13px] font-semibold"
                  style={{ color: '#a39fa0' }}
                >
                  Archived ({archivedNotes.length})
                  <ChevronDown className={`w-4 h-4 transition-transform ${showArchived ? 'rotate-180' : ''}`} />
                </button>
                {showArchived && (
                  <div className={cardCls + ' p-4'}>
                    {archivedNotes.map((n, i) => (
                      <button
                        key={n.id}
                        onClick={() => setNoteSheet({ note: n, kind: n.kind, mode: 'read' })}
                        className={'w-full flex items-center gap-3 py-3 text-left'
                          + (i === 0 ? '' : ' border-t theme-border-100')}
                      >
                        <span className="flex-1 min-w-0">
                          <span className="block text-[14px] font-semibold truncate" style={{ color: '#6B7280' }}>
                            {n.title || 'Untitled'}
                          </span>
                          <span className="block text-[11.5px] text-gray-400 mt-0.5">
                            {n.kind === 'pinned' ? 'Pinned note' : 'Note'}
                            {n.createdAt ? ' \u00b7 ' + prettyDate(n.createdAt) : ''}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div style={{ height: '8px' }} />
      </div>
      )}

      {/* plan_20: a folder opens on top of your notes, so you can swipe back to them */}
      {plan && openFolderObj && !budgetOpen && (
        <div ref={folderLayerRef} data-swipe-layer="1" style={{ position: 'fixed', inset: 0, zIndex: 35, background: '#F4F1FB', overflowY: 'auto', overscrollBehavior: 'contain', padding: 'env(safe-area-inset-top, 0px) 16px calc(env(safe-area-inset-bottom, 0px) + 110px)' }}>
        <div className="px-1 pt-3">
          <div className="flex items-center justify-between">
            <button onClick={() => setOpenFolder(null)} aria-label="Back" className="flex items-center justify-center"
              style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#FFFFFF' }}>
              <ChevronLeft className="w-5 h-5" style={{ color: '#26252E' }} />
            </button>
            <button onClick={() => setFolderSheet({ folder: openFolderObj })} aria-label="Folder options" className="flex items-center justify-center"
              style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#FFFFFF' }}>
              <MoreHorizontal className="w-5 h-5" style={{ color: '#26252E' }} />
            </button>
          </div>
          {(() => {
            const fe = planEmojiSplit(openFolderObj.name, '\ud83d\uddc3\ufe0f', true);
            return (
              <h1 className="m-0" style={{ fontSize: '32px', fontWeight: 600, lineHeight: 1.1, color: '#26252E', marginTop: '22px', overflowWrap: 'anywhere' }}>
                {(fe.text || openFolderObj.name).toLowerCase()} <span style={{ fontSize: '28px' }}>{fe.emoji}</span>
              </h1>
            );
          })()}
          <p className="m-0" style={{ fontSize: '15px', color: '#8B879A', marginTop: '6px' }}>
            {openFolderNotes.length} note{openFolderNotes.length === 1 ? '' : 's'}
          </p>
        </div>
        <div style={{ marginTop: '20px' }}>
          {openFolderNotes.length > 0 && (
            <div style={{ background: '#FFFFFF', borderRadius: '26px', padding: '6px 16px' }}>
              {openFolderNotes.map((n, i) => {
                const ne = planEmojiSplit(n.title, '\ud83d\udcdd');
                const prev = planPreview(n.body);
                const when = n.createdAt ? prettyShort(String(n.createdAt).slice(0, 10)) : '';
                return (
                  <button key={n.id} onClick={() => setNoteSheet({ note: n, kind: n.kind, mode: 'read' })}
                    className="w-full flex items-center text-left" style={{ gap: '13px', padding: '13px 0', background: 'transparent' }}>
                    <span style={{ width: '46px', height: '46px', borderRadius: '14px', background: PLAN_ROW_TONES[i % PLAN_ROW_TONES.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>{ne.emoji}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block" style={{ fontSize: '15.5px', fontWeight: 600, color: '#26252E', lineHeight: 1.3 }}>{ne.text || 'Untitled'}</span>
                      {(when || prev) && (
                        <span className="block" style={{ fontSize: '13px', color: '#9E9AAB', marginTop: '3px', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {[when, prev].filter(Boolean).join(' \u00b7 ')}
                        </span>
                      )}
                    </span>
                    <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#B9B5C6' }} />
                  </button>
                );
              })}
            </div>
          )}
          <button onClick={() => setNoteSheet({ note: null, kind: 'note', mode: 'edit', folderId: openFolderObj.id })}
            className="w-full flex items-center justify-center gap-1.5"
            style={{ marginTop: '14px', height: '54px', borderRadius: '999px', background: '#1F1E24', color: '#FFFFFF', fontSize: '16px', fontWeight: 600 }}>
            <Plus className="w-4 h-4" /> New note
          </button>
          <div style={{ height: '8px' }} />
        </div>
        </div>
      )}

      {/* Matches the Budget tab's FAB exactly - same size, offset and colours,
          so the gesture is the one people already know from elsewhere. */}
      {!anySheetOpen && tab !== 'budgets' && !(plan && openFolderObj) && (
        <button
          onClick={() => {
            if (tab === 'reminders') { setReminderSheet({ reminder: null }); return; }
            if (openFolderObj) {
              setNoteSheet({ note: null, kind: 'note', mode: 'edit', folderId: openFolderObj.id });
              return;
            }
            setShowAddMenu(true);
          }}
          className="fixed z-30 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 active:scale-95"
          style={{
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)',
            right: '20px',
            background: isPink
              ? 'linear-gradient(135deg, #F5A8C1 0%, #E27BA1 100%)'
              : isWarm ? '#2D5A3D' : '#4361EE',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
          data-testid="notes-fab"
        >
          <Plus className={`w-6 h-6 text-white transition-transform duration-200 ${showAddMenu ? 'rotate-45' : ''}`} />
        </button>
      )}

      {showAddMenu && (
        <Sheet
          title="Add to notes"
          subtitle="What do you want to create?"
          onClose={() => setShowAddMenu(false)}
          track={track}
          padBottom={96}
        >
          {[
            { key: 'pinned', emoji: '\ud83d\udccc', label: 'New pinned note', sub: 'A short rule or decision', slot: 0 },
            { key: 'note', emoji: '\ud83d\udcdd', label: 'New longer note', sub: 'Room to write more details', slot: 3 },
            { key: 'folder', emoji: '\ud83d\uddc2\ufe0f', label: 'New folder', sub: 'Group your longer notes', slot: 2 },
          ].map((o) => (
            <button
              key={o.key}
              onClick={() => {
                setShowAddMenu(false);
                if (o.key === 'folder') setFolderSheet({ folder: null });
                else setNoteSheet({ note: null, kind: o.key, mode: 'edit' });
              }}
              className="flex items-center gap-3.5 bg-white rounded-2xl px-4 py-3.5 shadow-sm w-full mb-2"
            >
              <span
                className="w-11 h-11 rounded-2xl flex items-center justify-center text-xl flex-shrink-0"
                style={{ background: tilePalette[o.slot].bg }}
              >
                {o.emoji}
              </span>
              <span className="flex flex-col items-start min-w-0">
                <span className="text-[15px] font-semibold text-gray-800">{o.label}</span>
                <span className="text-xs text-gray-400">{o.sub}</span>
              </span>
            </button>
          ))}
        </Sheet>
      )}

      {showInfo && createPortal(
        <div className="fixed inset-0 z-50" style={{ touchAction: 'none' }}>
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowInfo(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-10 shadow-xl">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
            <h3 className="font-normal text-gray-800 mb-4" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '20px' }}>About {tab === 'reminders' ? 'Reminders' : 'Notes'}</h3>
            <div className="space-y-4">
              {tab !== 'reminders' && (<div className="flex gap-3"><span className="text-xl flex-shrink-0">📌</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Pinned notes</p><p className="text-xs text-gray-500 leading-relaxed">Short rules and decisions you want to keep in view, each with a tag: Decided, Considering or Guardrail.</p></div></div>)}
              {tab !== 'reminders' && (<div className="flex gap-3"><span className="text-xl flex-shrink-0">📝</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Long notes</p><p className="text-xs text-gray-500 leading-relaxed">For longer notes and ideas. Enter starts a new line, and you can bold, colour or bullet parts of the text. Colours follow your theme, so they change with it. You have the option to group notes into folders as well.</p></div></div>)}
              {tab === 'reminders' && (<div className="flex gap-3"><span className="text-xl flex-shrink-0">⏰</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Reminders</p><p className="text-xs text-gray-500 leading-relaxed">Add a reminder and a due date, and you'll get a notification when it's due if reminders are switched on for Worthly.</p><p className="text-xs text-gray-500 leading-relaxed mt-2">If you don't set a reminder time, you'll be reminded at 12pm that day.</p><p className="text-xs text-gray-500 leading-relaxed mt-2">Anything you don't tick off stays in the list and turns red, so it keeps reminding you after the notification has gone.</p><p className="text-xs text-gray-500 leading-relaxed mt-2">Soon covers the next 90 days; Later is everything after that.</p></div></div>)}
              {tab !== 'reminders' && (<div className="flex gap-3"><span className="text-xl flex-shrink-0">🗓️</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Stored by year</p><p className="text-xs text-gray-500 leading-relaxed">Notes belong to the year you wrote them in. Use the arrows to move between years, and when a new year is empty you can bring last year's notes forward.</p></div></div>)}
              {tab !== 'reminders' && (<div className="flex gap-3"><span className="text-xl flex-shrink-0">🗄️</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Archive instead of deleting</p><p className="text-xs text-gray-500 leading-relaxed">Archiving keeps a note without leaving it in the way - archived ones sit at the foot of the Notes tab. Deleting removes it for good.</p></div></div>)}
            </div>
            <button onClick={() => setShowInfo(false)} className="mt-6 w-full py-3 rounded-2xl theme-bg-500 text-white font-semibold text-sm">Got it</button>
          </div>
        </div>,
        document.body
      )}
      {folderSheet && (
        <FolderSheet
          key={(folderSheet.folder && folderSheet.folder.id) || 'new'}
          folder={folderSheet.folder}
          palette={tilePalette}
          accent={accent}
          track={track}
          muted={muted}
          onSave={saveFolder}
          onDelete={deleteFolder}
          onClose={() => setFolderSheet(null)}
        />
      )}
      {moveSheet && (
        <MoveSheet
          note={moveSheet.note}
          folders={folders}
          palette={tilePalette}
          accent={accent}
          track={track}
          onMove={moveNote}
          onClose={() => setMoveSheet(null)}
        />
      )}
      {copySheet && (
        <BringForwardSheet
          notes={(local.notes || []).filter((n) => n.year === taxYear - 1 && !n.archived)}
          taxYear={taxYear}
          accent={accent}
          track={track}
          onCopy={bringForward}
          onClose={() => setCopySheet(false)}
        />
      )}
      {appleSheet && (
        <Sheet
          title="Back Up to Apple Reminders"
          subtitle="A second place to find what's due."
          onClose={() => setAppleSheet(false)}
          track={track}
          padBottom={96}
        >
          <div className="space-y-3.5 mb-5">
            {[
              ['\ud83d\udccb', 'Your reminders are copied into a Worthly list in your Reminders app, with their dates, notes and links.'],
              ['\ud83d\udd14', "They'll alert you from the Reminders app too, so they still work even if you stop using Worthly."],
              ['\u2139\ufe0f', "It's an easy way to check your reminders in the Reminders app as well. To complete one, do that here in Worthly."],
              ['\ud83d\uddd1\ufe0f', "If you delete or complete a copy in the Reminders app, it won't come back on its own, but the reminder itself stays here in Worthly. Switch this backup off and on again to put any missing ones back."],
              ['\ud83d\udd12', 'Worthly never reads or changes any of your other reminders.'],
            ].map(([emoji, text]) => (
              <div key={text} className="flex gap-3">
                <span className="text-lg flex-shrink-0 leading-snug">{emoji}</span>
                <p className="m-0 text-[13px] text-gray-600 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>

          <button
            disabled={appleBusy}
            onClick={enableAppleBackup}
            className="w-full rounded-full py-4 text-[15px] font-bold text-white"
            style={{ background: appleBusy ? '#E0D6DC' : accent }}
          >
            {appleBusy ? 'Setting up\u2026' : 'Turn on'}
          </button>
          <p className="text-[11.5px] text-gray-400 text-center mt-3 leading-relaxed">
            iOS will ask for permission next.
          </p>
        </Sheet>
      )}

      {reminderSheet && (
        <ReminderSheet
          key={(reminderSheet.mode || 'edit') + ':' + ((reminderSheet.reminder && reminderSheet.reminder.id) || 'new')}
          reminder={reminderSheet.reminder}
          mode={reminderSheet.mode}
          onEdit={(r) => setReminderSheet({ reminder: r, mode: 'edit' })}
          accent={accent}
          track={track}
          muted={muted}
          onSave={saveReminder}
          onDelete={deleteReminder}
          onClose={() => setReminderSheet(null)}
        />
      )}
      {noteSheet && (
        <NoteSheet
          key={noteSheet.mode + ':' + ((noteSheet.note && noteSheet.note.id) || 'new')}
          note={noteSheet.note}
          kind={noteSheet.kind}
          mode={noteSheet.mode}
          palette={tilePalette}
          accent={accent}
          track={track}
          muted={muted}
          onSave={saveNote}
          onArchive={archiveNote}
          onDelete={deleteNote}
          onEdit={(n) => setNoteSheet({ note: n, kind: n.kind, mode: 'edit' })}
          onClose={() => setNoteSheet(null)}
          folderName={(() => {
            const n = noteSheet.note;
            if (!n) return null;
            if (n.kind === 'pinned') return 'Pinned';
            const f = folders.find((x) => x.id === n.folderId);
            return f ? planEmojiSplit(f.name, '', true).text || f.name : 'Notes';
          })()}
        />
      )}
    </div>
  );
};

export default NotesTab;
